/* ======================================================================
   BYOB Builder (Build Your Own Bundle v3)

   <byob-builder>       root: progress bubbles, filters, sort, cart bar, Storefront hydration
   <byob-product-card>  selectable product tile (server-rendered or hydrated)
   ====================================================================== */
(function () {
  "use strict";

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  var BYOB_COOKIE_DAYS = 7;
  /* Leave headroom for cookie name + attributes (~4KB browser limit). */
  var BYOB_COOKIE_MAX_BYTES = 3500;

  /* ---------- Cookie helpers (BYOB selection persistence) ---------- */
  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
    );
    return match ? decodeURIComponent(match[1]) : "";
  }

  function setCookie(name, value, days) {
    var maxAge = Math.max(0, Math.round((days || BYOB_COOKIE_DAYS) * 86400));
    var secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      "; Path=/" +
      "; Max-Age=" +
      maxAge +
      "; SameSite=Lax" +
      secure;
  }

  function deleteCookie(name) {
    document.cookie =
      name +
      "=; Path=/; Max-Age=0; SameSite=Lax" +
      (window.location.protocol === "https:" ? "; Secure" : "");
  }

  /* ---------- Money formatting (mirrors Shopify money filters) ---------- */
  function formatMoney(cents, format) {
    var value = (cents || 0) / 100;
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || "${{amount}}";

    function withCommas(number, precision, thousands, decimal) {
      number = (number || 0).toFixed(precision);
      var parts = number.split(".");
      var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      var decPart = parts[1] ? decimal + parts[1] : "";
      return intPart + decPart;
    }

    var match = fmt.match(placeholder);
    var body;
    switch (match && match[1]) {
      case "amount_no_decimals":
        body = withCommas(value, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        body = withCommas(value, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        body = withCommas(value, 0, ".", ",");
        break;
      case "amount_with_space_separator":
        body = withCommas(value, 2, " ", ",");
        break;
      case "amount_with_apostrophe_separator":
        body = withCommas(value, 2, "'", ".");
        break;
      default:
        body = withCommas(value, 2, ",", ".");
    }
    return fmt.replace(placeholder, body);
  }

  function toCentsFromAmount(amount) {
    return Math.round(parseFloat(amount) * 100);
  }

  function numericId(gid) {
    return String(gid).split("/").pop();
  }

  function escapeHtml(str) {
    return String(str == null ? "" : str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }
  function escapeAttr(str) {
    return escapeHtml(str).replace(/"/g, "&quot;");
  }

  /* Normalize a shape/filter value the same way collection tag handles are built. */
  function normalizeShapeKey(str) {
    return String(str == null ? "" : str)
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");
  }

  /**
   * Extract nail shape from product tags.
   * Collection filters use tags like "Nail Shape_Almond" (category_value).
   */
  function shapeFromTags(tags) {
    if (!tags || !tags.length) return "";
    for (var i = 0; i < tags.length; i++) {
      var tag = String(tags[i] || "").toLowerCase().trim();
      var idx = tag.lastIndexOf("_");
      if (idx <= 0) continue;
      var category = normalizeShapeKey(tag.slice(0, idx));
      var value = tag.slice(idx + 1).trim().toLowerCase();
      if (!value) continue;
      if (category === "nail-shape" || category === "nailshape") return value;
    }
    return "";
  }

  /** Match BYOB bubble filter against metafield shape and/or Nail Shape_* tags. */
  function matchesNailShapeFilter(product, filter) {
    if (!filter) return true;
    var wanted = normalizeShapeKey(filter);
    if (!wanted) return true;

    var shape = normalizeShapeKey(product.shape || shapeFromTags(product.tags));
    if (shape && shape === wanted) return true;

    var tags = product.tags || [];
    for (var i = 0; i < tags.length; i++) {
      var tag = String(tags[i] || "").toLowerCase().trim();
      if (
        tag === "nail shape_" + filter ||
        tag === "nail_shape_" + filter ||
        tag === "nail-shape_" + filter
      ) {
        return true;
      }
      var idx = tag.lastIndexOf("_");
      if (idx <= 0) continue;
      var category = normalizeShapeKey(tag.slice(0, idx));
      var value = normalizeShapeKey(tag.slice(idx + 1));
      if (value === wanted && (category === "nail-shape" || category === "nailshape")) {
        return true;
      }
    }
    return false;
  }

  /**
   * Parse theme "Label|value" lines into filter definitions.
   * Storefront matching uses product tags (Nail Shape_Label) and/or
   * custom.nail_shape metafield values — not theme settings as the data source.
   */
  function parseNailShapeFilterDefs(raw) {
    var defs = [];
    String(raw || "")
      .split("\n")
      .forEach(function (line) {
        line = line.trim();
        if (!line) return;
        var parts = line.split("|");
        var label = parts[0].trim();
        var value = (parts[1] || parts[0]).trim().toLowerCase();
        if (!value) return;
        defs.push({
          label: label,
          value: value,
          /* Canonical collection tag used by Glamnetic filters / Search & Discovery */
          tag: "Nail Shape_" + label
        });
      });
    return defs;
  }

  /** Shopify collection tag path: "Nail Shape_Almond" → "nail-shape_almond". */
  function collectionTagHandle(filterDef) {
    if (!filterDef) return "";
    if (filterDef.tag) {
      return String(filterDef.tag).toLowerCase().trim().replace(/\s+/g, "-");
    }
    var value = normalizeShapeKey(filterDef.value || filterDef.label);
    return value ? "nail-shape_" + value : "";
  }

  /** Build ProductFilter[] for collection.products (Storefront ProductFilter). */
  function buildCollectionProductFilters(filterDef) {
    if (!filterDef) return [];
    var filters = [];
    if (filterDef.tag) {
      filters.push({ tag: filterDef.tag });
    }
    return filters;
  }

  /**
   * Build Storefront products(query:) search string.
   * @see https://shopify.dev/docs/api/storefront/latest/queries/products
   */
  function buildProductsSearchQuery(filterDef) {
    var parts = ["available_for_sale:true", "tag_not:hide"];
    if (filterDef && filterDef.tag) {
      parts.unshift("tag:'" + String(filterDef.tag).replace(/'/g, "") + "'");
    }
    return parts.join(" ");
  }

  /**
   * Map UI sort values to Storefront sortKey + reverse.
   * Collection uses ProductCollectionSortKeys (CREATED).
   * products query uses ProductSortKeys (CREATED_AT).
   * @see https://shopify.dev/docs/api/storefront/latest/enums/ProductSortKeys
   * @see https://shopify.dev/docs/api/storefront/latest/enums/ProductCollectionSortKeys
   */
  function resolveSortOption(activeSort) {
    switch (activeSort) {
      case "PRICE:asc":
        return {
          collectionSortKey: "PRICE",
          productSortKey: "PRICE",
          reverse: false
        };
      case "PRICE:desc":
        return {
          collectionSortKey: "PRICE",
          productSortKey: "PRICE",
          reverse: true
        };
      case "TITLE":
        return {
          collectionSortKey: "TITLE",
          productSortKey: "TITLE",
          reverse: false
        };
      case "CREATED":
        /* Newest first */
        return {
          collectionSortKey: "CREATED",
          productSortKey: "CREATED_AT",
          reverse: true
        };
      case "BEST_SELLING":
      default:
        return {
          collectionSortKey: "BEST_SELLING",
          productSortKey: "BEST_SELLING",
          reverse: false
        };
    }
  }

  function parseOkendoSummary(value) {
    var rating = 0;
    var count = 0;
    if (!value) return { rating: 0, count: 0 };
    try {
      var data = typeof value === "string" ? JSON.parse(value) : value;
      rating = parseFloat(data.reviewAverageValue) || 0;
      count = parseInt(data.reviewCount, 10) || 0;
    } catch (e) {}
    return { rating: rating, count: count };
  }

  /** Map a Storefront Product node (+ first variant) into BYOB product shape. */
  function mapStorefrontProductNode(node, bestSellingIndex) {
    if (!node) return null;
    var tags = (node.tags || []).map(function (t) {
      return String(t).toLowerCase();
    });
    if (tags.indexOf("hide") !== -1) return null;
    var variant = node.variants && node.variants.nodes && node.variants.nodes[0];
    if (!variant) return null;
    var okendo = parseOkendoSummary(node.okendo && node.okendo.value);
    var shape = node.nailShape ? String(node.nailShape.value).toLowerCase().trim() : "";
    if (!shape) shape = shapeFromTags(tags);
    return {
      variantId: numericId(variant.id),
      productId: numericId(node.id),
      handle: node.handle,
      title: node.title,
      subtitle: node.subtitle ? String(node.subtitle.value || "") : "",
      customLabel: node.customLabel ? String(node.customLabel.value || "") : "",
      price: toCentsFromAmount(variant.price.amount),
      compareAtPrice: variant.compareAtPrice
        ? toCentsFromAmount(variant.compareAtPrice.amount)
        : 0,
      image: node.featuredImage ? node.featuredImage.url : "",
      shape: shape,
      tags: tags,
      rating: okendo.rating,
      ratingCount: okendo.count,
      available: node.availableForSale && variant.availableForSale,
      createdAt: node.createdAt,
      bestSellingIndex: bestSellingIndex
    };
  }

  var STOREFRONT_PRODUCT_FIELDS =
    "id title handle createdAt availableForSale " +
    "featuredImage{url(transform:{maxWidth:400}) altText} tags " +
    'nailShape:metafield(namespace:"custom",key:"nail_shape"){value} ' +
    'subtitle:metafield(namespace:"global",key:"subtitle"){value} ' +
    'customLabel:metafield(namespace:"custom",key:"custom_label"){value} ' +
    'okendo:metafield(namespace:"okendo",key:"summaryData"){value} ' +
    "variants(first:1){nodes{id availableForSale price{amount} compareAtPrice{amount}}}";

  function ratingMarkup(rating, count, isModern) {
    if (!isModern) {
      if (!count || !rating) return "";
      return (
        '<div class="okendo-rating-badge" style="position: absolute; top: 0; right: 0; background-color: #FBF6F3; color: black; padding: 4px 8px; border-radius: 0; font-size: 12px; font-weight: 600; z-index: 2; box-shadow: none;">' +
        '<span class="rating-star">★</span> ' +
        '<span class="rating-value">' +
        escapeHtml(String(Math.round(rating * 10) / 10)) +
        "</span></div>"
      );
    }

    var hasReviews = !!(count && rating);
    var pct = hasReviews ? (Math.max(0, Math.min(5, rating)) / 5) * 100 : 0;
    var display = hasReviews ? (Math.round(rating * 10) / 10).toFixed(1) : "";
    var aria = hasReviews ? "Rated " + display + " out of 5" : "No Reviews";
    var valueHtml = hasReviews
      ? '<span class="okendo-meta-rating__value">' + escapeHtml(display) + "</span>"
      : '<span class="okendo-meta-rating__value okendo-meta-rating__value--empty">No Reviews</span>';

    return (
      '<div class="okendo-rating-modern">' +
      '<div class="okendo-meta-rating okendo-rating-modern__meta" style="--okendo-meta-star-size:24px;--okendo-meta-fill:' +
      pct +
      '%;--okendo-meta-rating-color:#000000;" aria-label="' +
      escapeAttr(aria) +
      '">' +
      '<span class="okendo-meta-rating__stars" aria-hidden="true"></span>' +
      valueHtml +
      "</div></div>"
    );
  }

  function subtitleMarkup(subtitle, customLabel, isModern) {
    var full = String(subtitle || "").trim();
    var lbl = String(customLabel || "").trim();
    if (!full && !lbl) return "";
    if (!full && lbl) full = lbl;
    else if (isModern && lbl && full.indexOf(" - " + lbl) === -1) full = full + " - " + lbl;
    var truncateAt = 30;
    var inner;
    if (isModern && full.length > truncateAt) {
      inner =
        '<span class="sm:hidden">' +
        escapeHtml(full.slice(0, truncateAt) + "…") +
        '</span><span class="hidden sm:block">' +
        escapeHtml(full) +
        "</span>";
    } else {
      inner = escapeHtml(full);
    }
    return (
      '<div class="card__subtitle body-text">' +
      '<span class="sr-only">Variant: </span>' +
      inner +
      "</div>"
    );
  }

  function isModernCardStyle(style) {
    return style === "modern" || style === "modern-variant-grid";
  }

  var ICON_MINUS =
    '<svg aria-hidden="true" focusable="false" fill="none" width="10" viewBox="0 0 12 12"><path d="M0 6h12" stroke="currentColor" stroke-width="1.5"/></svg>';
  var ICON_PLUS =
    '<svg aria-hidden="true" focusable="false" fill="none" width="10" viewBox="0 0 12 12"><path d="M6 0v12M0 6h12" stroke="currentColor" stroke-width="1.5"/></svg>';

  /* ====================================================================
     <byob-product-card>
     ==================================================================== */
  var CARD_TEMPLATE = function (p, opts) {
    opts = opts || {};
    var isModern = !!opts.isModern;
    var atcPlus = opts.atcStyle === "plus";
    var ratingInInfo = isModern ? ratingMarkup(p.rating, p.ratingCount, true) : "";
    var ratingOnImage = isModern ? "" : ratingMarkup(p.rating, p.ratingCount, false);
    var qtyControls =
      '<button type="button" class="byob-card__qty-btn" data-byob-qty="-1" aria-label="Decrease quantity of ' +
      escapeAttr(p.title) +
      '">' +
      ICON_MINUS +
      "</button>" +
      '<input class="byob-card__qty-input" type="number" value="1" min="0" inputmode="numeric" data-byob-qty-input aria-label="Quantity of ' +
      escapeAttr(p.title) +
      '">' +
      '<button type="button" class="byob-card__qty-btn" data-byob-qty="1" aria-label="Increase quantity of ' +
      escapeAttr(p.title) +
      '">' +
      ICON_PLUS +
      "</button>";
    var img = p.image
      ? '<a href="' +
        ROOT +
        "products/" +
        encodeURIComponent(p.handle) +
        '" class="product-card__media" draggable="false" tabindex="-1" aria-hidden="true">' +
        '<img src="' +
        p.image +
        '" class="product-card__image product-card__image--primary aspect-square" loading="lazy" alt="' +
        escapeAttr(p.title) +
        '" draggable="false">' +
        "</a>"
      : "";
    var figureExtras = atcPlus
      ? '<button type="button" class="byob-card__add byob-card__add--plus" aria-label="Add ' +
        escapeAttr(p.title) +
        ' to your bundle"><span aria-hidden="true">+</span></button>' +
        '<div class="byob-card__qty byob-card__qty--compact" hidden>' +
        qtyControls +
        "</div>"
      : "";
    var infoCta = atcPlus
      ? ""
      : '<button type="button" class="button button--primary product-card__cta byob-card__add" aria-label="Add ' +
        escapeAttr(p.title) +
        ' to your bundle">Add now</button>' +
        '<div class="byob-card__qty" hidden>' +
        qtyControls +
        "</div>";
    return (
      '<div class="product-card__figure byob-card__figure" style="position: relative;">' +
      ratingOnImage +
      img +
      figureExtras +
      "</div>" +
      '<div class="product-card__info byob-card__info">' +
      ratingInInfo +
      '<div class="v-stack justify-items-center gap-2">' +
      '<div class="v-stack justify-items-center gap-1">' +
      '<a href="' +
      ROOT +
      "products/" +
      encodeURIComponent(p.handle) +
      '" class="product-title line-clamp" style="--line-clamp-count: 2;" data-instant>' +
      escapeHtml(p.title) +
      "</a>" +
      '<div class="subtitle-price-column">' +
      subtitleMarkup(p.subtitle, p.customLabel, isModern) +
      '<div class="card__price xflex xjustify-center xitems-baseline byob-card__pricing">' +
      '<span class="card__compare-price body-text" data-byob-price-compare hidden></span>' +
      '<span class="body-text byob-card__amount" data-byob-price-current></span>' +
      "</div>" +
      "</div>" +
      "</div>" +
      "</div>" +
      infoCta +
      "</div>"
    );
  };

  if (typeof HTMLElement === "function") {
    var ByobProductCard = class extends HTMLElement {
      static build(product, cardOpts) {
        cardOpts = cardOpts || {};
        var isModern = !!cardOpts.isModern;
        var align = cardOpts.align || "left";
        var atcStyle = cardOpts.atcStyle || "button";
        var el = document.createElement("byob-product-card");
        el.className = isModern
          ? "product-card product-card--modern product-card--align-" + align + " byob-card"
          : "product-card byob-card";
        el.dataset.variantId = product.variantId;
        el.dataset.productId = product.productId;
        el.dataset.handle = product.handle;
        el.dataset.price = product.price;
        el.dataset.compareAt = product.compareAtPrice || 0;
        el.dataset.title = product.title;
        el.dataset.subtitle = product.subtitle || "";
        el.dataset.customLabel = product.customLabel || "";
        el.dataset.image = product.image || "";
        el.dataset.shape = product.shape || "";
        el.dataset.tags = (product.tags || []).join(",");
        el.dataset.rating = product.rating || 0;
        el.dataset.ratingCount = product.ratingCount || 0;
        el.dataset.available = product.available ? "true" : "false";
        if (product.createdAt) el.dataset.createdAt = product.createdAt;
        if (product.bestSellingIndex != null) el.dataset.bestSellingIndex = product.bestSellingIndex;
        el.innerHTML = CARD_TEMPLATE(product, {
          isModern: isModern,
          align: align,
          atcStyle: atcStyle
        });
        return el;
      }

      get product() {
        var tags = (this.dataset.tags || "").toLowerCase().split(",").filter(Boolean);
        var shape = (this.dataset.shape || "").toLowerCase().trim();
        if (!shape) shape = shapeFromTags(tags);
        var subtitle = (this.dataset.subtitle || "").trim();
        var customLabel = (this.dataset.customLabel || "").trim();
        /* Fallback: read visible subtitle from Liquid-rendered markup */
        if (!subtitle) {
          var subEl = this.querySelector(".card__subtitle");
          if (subEl) {
            var clone = subEl.cloneNode(true);
            var sr = clone.querySelector(".sr-only");
            if (sr) sr.remove();
            var desktop = clone.querySelector(".sm\\:block");
            var mobile = clone.querySelector(".sm\\:hidden");
            subtitle = String(
              (desktop && desktop.textContent) ||
                (mobile && mobile.textContent) ||
                clone.textContent ||
                ""
            ).trim();
            if (customLabel && subtitle.indexOf(" - " + customLabel) !== -1) {
              subtitle = subtitle.split(" - " + customLabel).join("").trim();
            }
          }
        }
        return {
          variantId: this.dataset.variantId,
          productId: this.dataset.productId,
          handle: this.dataset.handle,
          title: this.dataset.title,
          subtitle: subtitle,
          customLabel: customLabel,
          price: parseInt(this.dataset.price, 10) || 0,
          compareAtPrice: parseInt(this.dataset.compareAt, 10) || 0,
          image: this.dataset.image || "",
          shape: shape,
          tags: tags,
          rating: parseFloat(this.dataset.rating) || 0,
          ratingCount: parseInt(this.dataset.ratingCount, 10) || 0,
          available: this.dataset.available !== "false",
          createdAt: this.dataset.createdAt || null,
          bestSellingIndex: this.dataset.bestSellingIndex ? parseInt(this.dataset.bestSellingIndex, 10) : 9999
        };
      }

      setSelected(selected, qty) {
        var isOn = !!selected;
        this.classList.toggle("is-selected", isOn);
        var qtyEl = this.querySelector(".byob-card__qty");
        var addBtn = this.querySelector(".byob-card__add");
        if (qtyEl) {
          qtyEl.hidden = !isOn;
          qtyEl.setAttribute("aria-hidden", isOn ? "false" : "true");
        }
        if (addBtn) {
          addBtn.hidden = isOn;
          addBtn.setAttribute("aria-hidden", isOn ? "true" : "false");
        }
        if (isOn) this.setQuantity(qty || 1);
      }

      setQuantity(qty, canIncrease) {
        var value = Math.max(0, parseInt(qty, 10) || 0);
        var input = this.querySelector("[data-byob-qty-input]");
        var dec = this.querySelector('[data-byob-qty="-1"]');
        var inc = this.querySelector('[data-byob-qty="1"]');
        if (input) {
          input.value = String(Math.max(1, value));
          input.max = "";
        }
        if (dec) dec.disabled = false;
        if (inc && typeof canIncrease === "boolean") inc.disabled = !canIncrease;
      }

      applyDiscount(pct, format, priceDisplay) {
        var base = parseInt(this.dataset.price, 10) || 0;
        var compareAt = parseInt(this.dataset.compareAt, 10) || 0;
        var current = this.querySelector("[data-byob-price-current]");
        var compare = this.querySelector("[data-byob-price-compare]");
        if (!current) return;

        /* Regular mode: never show tier discounts on cards; show compare-at/cap when present. */
        if (priceDisplay === "regular") {
          current.textContent = formatMoney(base, format);
          current.classList.remove("card__discount-price", "is-discounted");
          if (compare) {
            if (compareAt > base) {
              compare.textContent = formatMoney(compareAt, format);
              compare.hidden = false;
            } else {
              compare.hidden = true;
              compare.textContent = "";
            }
          }
          return;
        }

        if (pct > 0) {
          current.textContent = formatMoney(Math.round(base * (1 - pct / 100)), format);
          current.classList.add("card__discount-price", "is-discounted");
          if (compare) {
            compare.textContent = formatMoney(base, format);
            compare.hidden = false;
          }
        } else {
          current.textContent = formatMoney(base, format);
          current.classList.remove("card__discount-price", "is-discounted");
          if (compare) {
            if (compareAt > base) {
              compare.textContent = formatMoney(compareAt, format);
              compare.hidden = false;
            } else {
              compare.hidden = true;
            }
          }
        }
      }
    };
    if (!window.customElements.get("byob-product-card")) {
      window.customElements.define("byob-product-card", ByobProductCard);
    }

    /* ==================================================================
       <byob-builder>
       ================================================================== */
    var ByobBuilder = class extends HTMLElement {
      connectedCallback() {
        var configEl = this.querySelector("[data-byob-config]");
        try {
          this.config = configEl ? JSON.parse(configEl.textContent) : {};
        } catch (e) {
          this.config = {};
        }
        this.moneyFormat = this.config.moneyFormat || "${{amount}}";
        this.bundleMax = parseInt(this.dataset.bundleMax, 10) || this.config.bundleMax || 10;
        this.progressVisibleSlots =
          parseInt(this.dataset.progressVisible, 10) ||
          this.config.progressVisibleSlots ||
          6;
        this.progressAnchor =
          this.dataset.progressAnchor || this.config.progressAnchor || "top";
        this.tiers = this.normalizeTiers(this.config.tiers);
        this.selection = new Map();
        this.activeFilter = "";
        this.activeSort = "BEST_SELLING";
        this.currentPage = 1;
        this.productsPerPage = Math.max(1, parseInt(this.config.productsPerPage, 10) || 12);
        this.enableLoadMore = !!this.config.enableLoadMore;
        this.loadMoreButtonLabel = this.config.loadMoreButtonLabel || "Load more";
        this.showLoadMoreCount = this.config.showLoadMoreCount !== false;
        this.atcStyle = this.config.atcStyle || this.dataset.atcStyle || "button";
        this.priceDisplay = this.config.priceDisplay || this.dataset.priceDisplay || "dynamic";
        this.emptyCalloutEnabled = !!this.config.emptyCalloutEnabled;
        this.filterDefs = parseNailShapeFilterDefs(this.config.nailShapes);
        this.filterRequestId = 0;
        /* When set, grid uses Storefront filter/sort results instead of local catalog */
        this.apiViewProducts = null;
        this._filteredCache = null;
        this._remotePage = 0;
        this._remotePages = 0;
        this._remoteCount = 0;
        this._remoteTagHandle = "";
        this.activeCatalog = "nails";
        this.nailsCatalogCache = null;
        this.accessoriesCatalogCache = null;
        this.config.nailsCollectionHandle =
          this.config.nailsCollectionHandle ||
          this.config.collectionHandle ||
          this.config.defaultCollectionHandle ||
          "";
        this.config.accessoriesCollectionHandle =
          this.config.accessoriesCollectionHandle || "";
        this.storefrontReady = !!(this.config.token && this.getActiveCollectionHandle());

        this.els = {
          grid: this.querySelector("[data-byob-grid]"),
          gridShell: this.querySelector("[data-byob-grid-shell]"),
          toolbar: this.querySelector(".byob-toolbar"),
          filters: this.querySelector("[data-byob-filters]"),
          filterPanel: this.querySelector("[data-byob-filter-panel]"),
          sort: this.querySelector("[data-byob-sort]"),
          pagination: this.querySelector("[data-byob-pagination]"),
          results: this.querySelector("[data-byob-results]"),
          count: this.querySelector("[data-byob-count]"),
          bar: this.querySelector("[data-byob-bar]"),
          ticks: this.querySelector("[data-byob-ticks]"),
          scroller: this.querySelector("[data-byob-scroller]"),
          scrollPrev: this.querySelector("[data-byob-scroll-prev]"),
          scrollNext: this.querySelector("[data-byob-scroll-next]"),
          catalogSwitcher: this.querySelector("[data-byob-catalog-switcher]"),
          showAccessories: this.querySelector("[data-byob-show-accessories]"),
          total: this.querySelector("[data-byob-total]"),
          savings: this.querySelector("[data-byob-savings]"),
          addBtn: this.querySelector("[data-byob-add]"),
          status: this.querySelector("[data-byob-status]"),
          cartBar: this.querySelector("[data-byob-cart-bar]"),
          cartPanel: this.querySelector("[data-byob-cart-panel]"),
          cartToggle: this.querySelector("[data-byob-cart-toggle]"),
          cartToggleLabel: this.querySelector("[data-byob-cart-toggle-label]"),
          cartDetails: this.querySelector("[data-byob-cart-details]"),
          cartList: this.querySelector("[data-byob-cart-list]"),
          progress: this.querySelector("[data-byob-progress]"),
          loadMore: this.querySelector("[data-byob-load-more]"),
          loadMoreButton: this.querySelector("[data-byob-load-more-button]"),
          loadMoreCount: this.querySelector("[data-byob-load-more-count]"),
          loadMoreLabel: this.querySelector("[data-byob-load-more-label]"),
          emptyCallout: this.querySelector("[data-byob-empty-callout]")
        };

        this.addButtonText =
          this.config.addButtonText ||
          (this.els.addBtn && this.els.addBtn.getAttribute("data-byob-add-default")) ||
          "Add bundle to cart";

        this.dataset_products = this.readInitialProducts();
        this._lastSelectionCount = 0;
        this.renderFilters();
        this.renderTicks();
        this.bindEvents();
        /*
         * Keep Liquid-rendered cards on first paint (correct subtitle markup).
         * Only sync prices/selection — applyView() rebuilds after hydrate/filter/sort.
         */
        this.renderPagination(this.getVisibleProducts().length);
        this.restoreSelectionFromCookie();
        this.syncAllCards();
        this.updateAllPrices();
        this.updateProgress();
        this.syncProgressScrollUi();
        this.updateBottomPad();
        this.resetRemotePaging("");
      }

      getActiveCollectionHandle() {
        if (this.activeCatalog === "accessories") {
          return this.config.accessoriesCollectionHandle || "";
        }
        return (
          this.config.nailsCollectionHandle ||
          this.config.collectionHandle ||
          this.config.defaultCollectionHandle ||
          ""
        );
      }

      setActiveCatalog(catalog) {
        var next = catalog === "accessories" ? "accessories" : "nails";
        if (next === "accessories" && !this.config.accessoriesCollectionHandle) return;
        if (next === this.activeCatalog) return;

        this.activeCatalog = next;
        this.config.collectionHandle = this.getActiveCollectionHandle();
        this.classList.toggle("is-accessories-catalog", next === "accessories");
        this.activeFilter = "";
        this.apiViewProducts = null;
        this._filteredCache = null;
        this.currentPage = 1;
        this.resetRemotePaging("");

        var reminder = this.querySelector("[data-byob-accessories-reminder]");
        if (reminder) reminder.hidden = next === "accessories";

        if (this.els.catalogSwitcher) {
          var buttons = this.els.catalogSwitcher.querySelectorAll("[data-byob-catalog]");
          Array.prototype.forEach.call(buttons, function (btn) {
            var on = btn.dataset.byobCatalog === next;
            btn.classList.toggle("is-active", on);
            btn.setAttribute("aria-selected", on ? "true" : "false");
          });
        }

        if (this.els.filters) {
          var filterBtns = this.els.filters.querySelectorAll(".byob-filter");
          Array.prototype.forEach.call(filterBtns, function (btn) {
            var isAll = !btn.dataset.byobFilter;
            btn.classList.toggle("is-active", isAll);
            btn.setAttribute("aria-pressed", isAll ? "true" : "false");
          });
        }

        /* Prefer cached catalog for instant switch; otherwise hydrate */
        var cached =
          next === "accessories" ? this.accessoriesCatalogCache : this.nailsCatalogCache;
        if (cached && cached.length) {
          this.dataset_products = cached.slice();
          this.applyView();
          this.enrichSelectionFromCatalog();
          return;
        }

        this.dataset_products = [];
        this.loadFirstRemotePage();
      }

      /* ---------- Selection cookie persistence ---------- */
      getSelectionCookieKey() {
        var handle =
          (this.config &&
            (this.config.nailsCollectionHandle || this.config.collectionHandle)) ||
          "default";
        return "byob_sel_" + String(handle).replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 40);
      }

      serializeSelectionForCookie() {
        var items = [];
        this.selection.forEach(function (item) {
          items.push({
            v: String(item.variantId),
            q: item.qty || 1,
            t: item.title || "",
            p: item.price || 0,
            i: item.image || "",
            h: item.handle || "",
            s: item.subtitle || ""
          });
        });
        return items;
      }

      saveSelectionCookie() {
        var key = this.getSelectionCookieKey();
        var items = this.serializeSelectionForCookie();
        if (!items.length) {
          deleteCookie(key);
          return;
        }
        var payload = JSON.stringify(items);
        /* Prefer full snapshot; fall back to id+qty if payload is too large for a cookie. */
        if (payload.length > BYOB_COOKIE_MAX_BYTES) {
          payload = JSON.stringify(
            items.map(function (it) {
              return { v: it.v, q: it.q };
            })
          );
        }
        if (payload.length > BYOB_COOKIE_MAX_BYTES) {
          deleteCookie(key);
          return;
        }
        setCookie(key, payload, BYOB_COOKIE_DAYS);
      }

      readSelectionCookie() {
        var raw = getCookie(this.getSelectionCookieKey());
        if (!raw) return [];
        try {
          var parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }

      findProductByVariantId(variantId) {
        var id = String(variantId);
        var products = this.dataset_products || [];
        for (var i = 0; i < products.length; i++) {
          if (String(products[i].variantId) === id) return products[i];
        }
        var cards = this.querySelectorAll("byob-product-card");
        for (var c = 0; c < cards.length; c++) {
          if (String(cards[c].dataset.variantId) === id) return cards[c].product;
        }
        return null;
      }

      restoreSelectionFromCookie() {
        var stored = this.readSelectionCookie();
        if (!stored.length) return;

        var max = this.bundleMax;
        var total = 0;
        this.selection.clear();

        for (var i = 0; i < stored.length; i++) {
          var row = stored[i] || {};
          var variantId = String(row.v || row.variantId || "");
          var qty = Math.max(0, parseInt(row.q || row.qty, 10) || 0);
          if (!variantId || qty <= 0) continue;
          if (total >= max) break;
          if (total + qty > max) qty = max - total;
          if (qty <= 0) break;

          var catalog = this.findProductByVariantId(variantId);
          var item = catalog
            ? Object.assign({}, catalog, { qty: qty })
            : {
                variantId: variantId,
                productId: "",
                handle: row.h || "",
                title: row.t || "Product",
                subtitle: row.s || "",
                customLabel: "",
                price: parseInt(row.p, 10) || 0,
                image: row.i || "",
                shape: "",
                tags: [],
                rating: 0,
                ratingCount: 0,
                available: true,
                qty: qty
              };

          this.selection.set(variantId, item);
          total += qty;
        }
      }

      /**
       * After catalog hydrate, refresh restored items with live product data (price/image/title).
       */
      enrichSelectionFromCatalog() {
        if (!this.selection.size) return;
        var self = this;
        var changed = false;
        this.selection.forEach(function (item, id) {
          var live = self.findProductByVariantId(id);
          if (!live) return;
          self.selection.set(
            id,
            Object.assign({}, live, { qty: item.qty || 1 })
          );
          changed = true;
        });
        if (changed) {
          this.syncAllCards();
          this.updateProgress();
          this.updateAllPrices();
          this.saveSelectionCookie();
        }
      }

      normalizeTiers(tiers) {
        var parsed = [];

        if (typeof tiers === "string") {
          var raw = tiers.trim();
          if (!raw) return [];

          /* Preferred: "3/10,4/15,5/20" */
          if (raw.indexOf("/") !== -1) {
            raw.split(",").forEach(function (pair) {
              pair = String(pair || "").trim();
              if (!pair) return;
              var parts = pair.split("/");
              var minQty = parseInt(parts[0], 10) || 0;
              var pct = parseFloat(parts[1]) || 0;
              if (minQty > 0) {
                parsed.push({ min_qty: minQty, discount_pct: pct });
              }
            });
            return parsed.sort(function (a, b) {
              return a.min_qty - b.min_qty;
            });
          }

          /* Legacy JSON string support */
          try {
            tiers = JSON.parse(raw);
          } catch (e) {
            return [];
          }
        }

        if (!Array.isArray(tiers)) return [];
        return tiers
          .map(function (t) {
            return {
              min_qty: parseInt(t.min_qty, 10) || 0,
              discount_pct: parseFloat(t.discount_pct) || 0
            };
          })
          .filter(function (t) {
            return t.min_qty > 0;
          })
          .sort(function (a, b) {
            return a.min_qty - b.min_qty;
          });
      }

      readInitialProducts() {
        var out = [];
        var seen = {};
        var cards = this.els.grid ? this.els.grid.querySelectorAll("byob-product-card") : [];
        Array.prototype.forEach.call(cards, function (card) {
          var p = card.product;
          if (seen[p.variantId]) return;
          seen[p.variantId] = true;
          out.push(p);
        });
        return out;
      }

      /* ---------- Filters UI ---------- */
      renderFilters() {
        if (!this.els.filters) return;
        var defs = this.filterDefs || [];
        if (!defs.length) return;
        var frag = document.createDocumentFragment();
        defs.forEach(function (def) {
          var btn = document.createElement("button");
          btn.type = "button";
          btn.className = "byob-filter";
          btn.dataset.byobFilter = def.value;
          btn.dataset.byobFilterTag = def.tag;
          btn.setAttribute("aria-pressed", "false");
          btn.textContent = def.label;
          frag.appendChild(btn);
        });
        this.els.filters.appendChild(frag);
      }

      getActiveFilterDef() {
        if (this.activeCatalog === "accessories") return null;
        var value = this.activeFilter;
        if (!value) return null;
        var defs = this.filterDefs || [];
        for (var i = 0; i < defs.length; i++) {
          if (defs[i].value === value) return defs[i];
        }
        return { label: value, value: value, tag: "Nail Shape_" + value };
      }

      resetRemotePaging(tagHandle) {
        this._remoteTagHandle = tagHandle || "";
        this._remotePage = 0;
        this._remotePages = 0;
        this._remoteCount = this._remoteTagHandle ? 0 : this.expectedCatalogCount();
      }

      remoteDisplayTotal(localCount) {
        if (this._remoteCount > 0) return this._remoteCount;
        if (this.activeFilter) return localCount || 0;
        return Math.max(localCount || 0, this.expectedCatalogCount());
      }

      hasMoreRemotePages() {
        if (this._remotePages > 0) return this._remotePage < this._remotePages;
        var expected = this.remoteDisplayTotal(this.getVisibleProducts().length);
        return this.getVisibleProducts().length < expected;
      }

      /* ---------- Progress slots (titles, +, discounts between) ---------- */
      renderTicks() {
        if (!this.els.ticks) return;
        if (this.progressAnchor === "right" && this.isStickyRightDesktop()) {
          this.renderCardGridTicks();
          return;
        }
        this.els.ticks.classList.remove("byob-bundle__ticks--card-grid");
        var max = this.bundleMax;
        var tierByQty = {};
        this.tiers.forEach(function (t) {
          tierByQty[t.min_qty] = t;
        });

        var html = "";
        for (var n = 1; n <= max; n++) {
          if (n > 1) {
            var tier = tierByQty[n];
            html +=
              '<div class="byob-tick-gap' +
              (tier ? " byob-tick-gap--tier" : "") +
              '" data-gap-min="' +
              n +
              '" aria-hidden="true">' +
              '<span class="byob-tick-gap__line"></span>' +
              (tier
                ? '<span class="byob-tick-gap__pct" data-byob-gap-pct>' +
                  tier.discount_pct +
                  "%</span>"
                : "") +
              "</div>";
          }

          html +=
            '<div class="byob-tick byob-tick--empty" role="listitem" data-slot="' +
            n +
            '" aria-label="' +
            (n <= 2 ? "Add product to slot " + n + " of " + max : "Empty slot " + n + " of " + max) +
            '">' +
            '<span class="byob-tick__dot">' +
            this.emptyTickDotHtml(n) +
            "</span>" +
            '<button type="button" class="byob-tick__remove" data-byob-tick-remove hidden aria-label="Remove from bundle">' +
            '<span aria-hidden="true">&times;</span>' +
            "</button>" +
            '<span class="byob-tick__label" data-byob-tick-label></span>' +
            "</div>";
        }
        this.els.ticks.innerHTML = html;
      }

      renderCardGridTicks() {
        if (!this.els.ticks) return;
        var max = this.bundleMax;
        var tierByQty = {};
        this.tiers.forEach(function (t) {
          tierByQty[t.min_qty] = t;
        });

        var html = "";
        for (var n = 1; n <= max; n++) {
          var tier = tierByQty[n];
          html +=
            '<div class="byob-tick byob-tick--card byob-tick--empty" role="listitem" data-slot="' +
            n +
            '" aria-label="Empty slot ' +
            n +
            " of " +
            max +
            '">' +
            '<span class="byob-tick__index" aria-hidden="true">' +
            n +
            "</span>" +
            '<button type="button" class="byob-tick__remove" data-byob-tick-remove hidden aria-label="Remove from bundle">' +
            '<span aria-hidden="true">&times;</span>' +
            "</button>" +
            '<div class="byob-tick__body">' +
            (tier
              ? '<span class="byob-tick__discount" data-byob-tick-discount>' +
                tier.discount_pct +
                "% OFF</span>"
              : '<span class="byob-tick__discount" data-byob-tick-discount hidden></span>') +
            '<span class="byob-tick__dot" aria-hidden="true"></span>' +
            "</div>" +
            '<span class="byob-tick__hint" data-byob-tick-hint>Select Product</span>' +
            '<span class="byob-tick__label" data-byob-tick-label hidden></span>' +
            "</div>";
        }
        this.els.ticks.innerHTML = html;
        this.els.ticks.classList.add("byob-bundle__ticks--card-grid");
      }

      truncateProgressTitle(title) {
        return String(title || "").trim();
      }

      emptyTickDotHtml(slotNumber) {
        if (this.isStickyRightDesktop()) {
          return '<span class="byob-tick__plus" aria-hidden="true">+</span>';
        }
        if (slotNumber <= 2) {
          return (
            '<span class="byob-tick__add-text" aria-hidden="true">' +
            '<span class="byob-tick__add-text-line">Add</span>' +
            '<span class="byob-tick__add-text-line">Product</span>' +
            "</span>"
          );
        }
        return '<span class="byob-tick__plus" aria-hidden="true">+</span>';
      }

      /* ---------- Events ---------- */
      bindEvents() {
        var self = this;

        if (this.els.grid) {
          this.els.grid.addEventListener("click", function (e) {
            var qtyBtn = e.target.closest("[data-byob-qty]");
            if (qtyBtn) {
              e.preventDefault();
              var qtyCard = qtyBtn.closest("byob-product-card");
              if (qtyCard) self.adjustQuantity(qtyCard, parseInt(qtyBtn.dataset.byobQty, 10) || 0);
              return;
            }
            var btn = e.target.closest(".byob-card__add");
            if (!btn) return;
            e.preventDefault();
            var card = btn.closest("byob-product-card");
            if (card) self.addCard(card);
          });

          this.els.grid.addEventListener("change", function (e) {
            var input = e.target.closest("[data-byob-qty-input]");
            if (!input) return;
            var card = input.closest("byob-product-card");
            if (!card) return;
            self.setCardQuantity(card, parseInt(input.value, 10) || 0);
          });
        }

        if (this.els.filters) {
          this.els.filters.addEventListener("click", function (e) {
            var btn = e.target.closest(".byob-filter");
            if (!btn) return;
            self.setFilter(btn.dataset.byobFilter || "");
          });
        }

        if (this.els.catalogSwitcher) {
          this.els.catalogSwitcher.addEventListener("click", function (e) {
            var btn = e.target.closest("[data-byob-catalog]");
            if (!btn) return;
            self.setActiveCatalog(btn.dataset.byobCatalog || "nails");
          });
        }

        if (this.els.showAccessories) {
          this.els.showAccessories.addEventListener("click", function () {
            self.setActiveCatalog("accessories");
            if (self.els.catalogSwitcher && typeof self.els.catalogSwitcher.scrollIntoView === "function") {
              self.els.catalogSwitcher.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
          });
        }

        if (this.els.scrollPrev) {
          this.els.scrollPrev.addEventListener("click", function () {
            self.scrollProgressSlots(-1);
          });
        }
        if (this.els.scrollNext) {
          this.els.scrollNext.addEventListener("click", function () {
            self.scrollProgressSlots(1);
          });
        }
        if (this.els.scroller) {
          this.els.scroller.addEventListener("scroll", function () {
            self.syncProgressScrollUi();
          });
          if (typeof ResizeObserver !== "undefined") {
            this._progressResizeObserver = new ResizeObserver(function () {
              self.syncProgressScrollUi();
              self.updateBottomPad();
            });
            this._progressResizeObserver.observe(this.els.scroller);
            if (this.els.progress) this._progressResizeObserver.observe(this.els.progress);
          }
          window.addEventListener("resize", function () {
            self.syncProgressScrollUi();
            self.updateBottomPad();
            /* Sticky-right tick layout depends on desktop breakpoint */
            if (self.progressAnchor === "right") {
              self.renderTicks();
              self.updateProgress();
            }
          });
        }

        if (this.els.sort) {
          this.els.sort.addEventListener("change", function () {
            self.activeSort = self.els.sort.value;
            self.currentPage = 1;
            self.refreshCatalogView();
          });
        }

        if (this.els.pagination) {
          this.els.pagination.addEventListener("click", function (e) {
            var btn = e.target.closest("[data-byob-page]");
            if (!btn || btn.hasAttribute("aria-current")) return;
            e.preventDefault();
            var page = parseInt(btn.getAttribute("data-byob-page"), 10);
            if (!page || page === self.currentPage) return;
            self.currentPage = page;
            self.applyView({ scroll: true });
          });
        }

        if (this.els.loadMoreButton) {
          this.els.loadMoreButton.addEventListener("click", function () {
            if (self.els.loadMore && self.els.loadMore.classList.contains("is-loading")) return;
            self.loadMoreProducts();
          });
        }

        if (this.els.addBtn) {
          this.els.addBtn.addEventListener("click", function () {
            self.addBundleToCart();
          });
        }

        if (this.els.cartToggle && this.els.cartPanel) {
          this.els.cartToggle.addEventListener("click", function () {
            var expanded = !self.els.cartPanel.classList.contains("is-expanded");
            if (expanded) {
              self.expandCartDetails();
            } else {
              self.collapseCartDetails();
            }
          });
        }

        /* Details list may sit outside the cart panel (full-width row) */
        var cartDetailsHost =
          this.els.cartDetails || this.querySelector("[data-byob-cart-details]");
        this.els.cartDetails = cartDetailsHost;
        if (!this.els.cartList && cartDetailsHost) {
          this.els.cartList = cartDetailsHost.querySelector("[data-byob-cart-list]");
        }

        if (this.els.cartList) {
          this.els.cartList.addEventListener("click", function (e) {
            var removeBtn = e.target.closest("[data-byob-cart-remove]");
            if (!removeBtn) return;
            e.preventDefault();
            var variantId = removeBtn.getAttribute("data-byob-cart-remove");
            if (variantId) self.decrementVariant(variantId);
          });
        }

        if (this.els.ticks) {
          this.els.ticks.addEventListener("click", function (e) {
            var removeBtn = e.target.closest("[data-byob-tick-remove]");
            if (!removeBtn) return;
            e.preventDefault();
            e.stopPropagation();
            var tick = removeBtn.closest(".byob-tick");
            var variantId = tick && tick.getAttribute("data-variant-id");
            if (variantId) self.decrementVariant(variantId);
          });
        }

      }

      /* ---------- Selection ---------- */
      getSelectionCount() {
        var total = 0;
        this.selection.forEach(function (item) {
          total += item.qty || 0;
        });
        return total;
      }

      getSelectionItems() {
        var items = [];
        this.selection.forEach(function (item) {
          for (var i = 0; i < item.qty; i++) items.push(item);
        });
        return items;
      }

      syncCard(card) {
        if (!card) return;
        var id = card.dataset.variantId;
        var item = this.selection.get(id);
        var room = this.bundleMax - this.getSelectionCount();
        var atLimit = room <= 0;
        var addBtn = card.querySelector(".byob-card__add");
        if (item) {
          card.setSelected(true, item.qty);
          card.setQuantity(item.qty, room > 0);
          if (addBtn) addBtn.disabled = false;
        } else {
          card.setSelected(false);
          if (addBtn) addBtn.disabled = atLimit;
        }
      }

      syncAllCards() {
        var self = this;
        var cards = this.querySelectorAll("byob-product-card");
        Array.prototype.forEach.call(cards, function (card) {
          self.syncCard(card);
        });
      }

      addCard(card) {
        var p = card.product;
        if (this.selection.has(p.variantId)) return;
        if (this.getSelectionCount() >= this.bundleMax) {
          this.flashLimit();
          return;
        }
        this.selection.set(p.variantId, Object.assign({}, p, { qty: 1 }));
        this.afterSelectionChange();
      }

      adjustQuantity(card, delta) {
        var id = card.dataset.variantId;
        var item = this.selection.get(id);
        if (!item) return;
        this.setCardQuantity(card, item.qty + delta);
      }

      setCardQuantity(card, nextQty) {
        var id = card.dataset.variantId;
        var item = this.selection.get(id);
        if (!item) return;

        var qty = Math.max(0, parseInt(nextQty, 10) || 0);
        if (qty === 0) {
          this.removeVariant(id);
          return;
        }

        var others = this.getSelectionCount() - item.qty;
        if (others + qty > this.bundleMax) {
          qty = this.bundleMax - others;
          this.flashLimit();
          if (qty <= 0) {
            this.removeVariant(id);
            return;
          }
        }

        item.qty = qty;
        this.selection.set(id, item);
        this.afterSelectionChange();
      }

      removeVariant(variantId) {
        this.selection.delete(variantId);
        this.afterSelectionChange();
      }

      decrementVariant(variantId) {
        var item = this.selection.get(variantId);
        if (!item) return;
        if (item.qty <= 1) {
          this.removeVariant(variantId);
          return;
        }
        item.qty -= 1;
        this.selection.set(variantId, item);
        this.afterSelectionChange();
      }

      afterSelectionChange() {
        var prevCount = this._lastSelectionCount || 0;
        this.syncAllCards();
        this.updateProgress();
        this.updateAllPrices();
        this.saveSelectionCookie();
        var nextCount = this.getSelectionCount();
        if (nextCount > prevCount) {
          this.maybeAutoScrollProgress();
          this.scrollProgressToLatest();
        }
        this._lastSelectionCount = nextCount;
      }

      scrollProgressToLatest() {
        var scroller = this.els.scroller;
        var ticks = this.els.ticks;
        if (!scroller || !ticks) return;
        var count = this.getSelectionCount();
        if (count <= 0) return;
        var tick = ticks.querySelectorAll(".byob-tick")[count - 1];
        if (!tick || typeof tick.scrollIntoView !== "function") return;
        tick.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
      }

      /**
       * On smaller viewports, bring the sticky progress into view after an add
       * so users can see the updated slots without hunting.
       */
      maybeAutoScrollProgress() {
        if (typeof window.matchMedia !== "function") return;
        if (!window.matchMedia("(max-width: 440px)").matches) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        var progress =
          this.querySelector("[data-byob-progress]") || this.els.cartBar;
        if (!progress || typeof progress.scrollIntoView !== "function") return;
        var rect = progress.getBoundingClientRect();
        /* Already fully in the sticky band — skip. */
        if (rect.top >= 0 && rect.top < 120) return;
        progress.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      flashLimit() {
        if (this.els.count) {
          this.els.count.animate(
            [{ transform: "scale(1)" }, { transform: "scale(1.25)" }, { transform: "scale(1)" }],
            { duration: 240 }
          );
        }
      }

      /* ---------- Discount tiers ---------- */
      currentDiscountPct() {
        var count = this.getSelectionCount();
        var pct = 0;
        this.tiers.forEach(function (t) {
          if (count >= t.min_qty) pct = t.discount_pct;
        });
        return pct;
      }

      /* ---------- Rendering: progress slots + cart bar ---------- */
      updateProgress() {
        var self = this;
        var count = this.getSelectionCount();
        var max = this.bundleMax;
        var pct = this.currentDiscountPct();
        var items = this.getSelectionItems();
        var fmt = this.moneyFormat;
        var tierByQty = {};
        this.tiers.forEach(function (t) {
          tierByQty[t.min_qty] = t;
        });

        if (this.els.count) this.els.count.textContent = String(count);

        if (this.els.ticks) {
          var ticks = this.els.ticks.querySelectorAll(".byob-tick");
          var cardGrid = this.isStickyRightDesktop();
          Array.prototype.forEach.call(ticks, function (tick, i) {
            var n = i + 1;
            var item = items[i];
            var dot = tick.querySelector(".byob-tick__dot");
            var label = tick.querySelector("[data-byob-tick-label]");
            var hint = tick.querySelector("[data-byob-tick-hint]");
            var discount = tick.querySelector("[data-byob-tick-discount]");
            var removeBtn = tick.querySelector("[data-byob-tick-remove]");
            var indexEl = tick.querySelector(".byob-tick__index");
            var filled = !!item;

            tick.classList.toggle("is-filled", filled);
            tick.classList.toggle("byob-tick--empty", !filled);
            tick.classList.toggle("is-reached", count >= n);

            if (cardGrid) {
              tick.hidden = false;
              tick.classList.remove("is-sidebar-next");
            } else {
              /* Sticky-right mobile / top / bottom: filled slots + next empty row only on desktop sidebar */
              var showInSidebar = !self.isStickyRightDesktop() || filled || n === count + 1;
              tick.hidden = !showInSidebar;
              tick.classList.toggle("is-sidebar-next", self.isStickyRightDesktop() && !filled && n === count + 1);
            }

            if (!dot) return;

            if (filled) {
              tick.setAttribute("data-variant-id", item.variantId);
              dot.innerHTML = item.image
                ? '<img src="' +
                  item.image +
                  '" alt="' +
                  escapeAttr(item.title) +
                  '" loading="lazy">'
                : "";
              if (label) {
                label.textContent = self.truncateProgressTitle(item.title);
                label.title = item.title;
                label.removeAttribute("hidden");
              }
              if (indexEl) indexEl.hidden = true;
              if (hint) hint.hidden = true;
              if (discount) discount.hidden = true;
              if (removeBtn) {
                removeBtn.hidden = false;
                removeBtn.setAttribute("aria-label", "Remove " + item.title + " from bundle");
              }
              tick.setAttribute("aria-label", item.title + " in slot " + n);
            } else {
              tick.removeAttribute("data-variant-id");
              if (cardGrid) {
                dot.innerHTML = "";
              } else {
                dot.innerHTML = self.emptyTickDotHtml(n);
              }
              if (label) {
                label.textContent = "";
                label.hidden = true;
                label.removeAttribute("title");
              }
              if (indexEl) indexEl.hidden = false;
              if (hint) {
                hint.hidden = false;
                hint.textContent = "Select Product";
              }
              if (discount) {
                var tier = tierByQty[n];
                if (tier) {
                  discount.hidden = false;
                  discount.textContent = tier.discount_pct + "% OFF";
                } else {
                  discount.hidden = true;
                  discount.textContent = "";
                }
              }
              if (removeBtn) {
                removeBtn.hidden = true;
                removeBtn.setAttribute("aria-label", "Remove from bundle");
              }
              tick.setAttribute("aria-label", "Empty slot " + n + " of " + max);
            }
          });

          /* Discount % between slots — hidden in card grid; bubble modes use gaps */
          var gaps = this.els.ticks.querySelectorAll(".byob-tick-gap");
          var stickyRight = this.isStickyRightDesktop();
          Array.prototype.forEach.call(gaps, function (gap) {
            if (cardGrid) {
              gap.hidden = true;
              return;
            }
            var min = parseInt(gap.getAttribute("data-gap-min"), 10) || 0;
            var tier = tierByQty[min];
            var pctEl = gap.querySelector("[data-byob-gap-pct]");
            var earned = !!(tier && count >= tier.min_qty);
            gap.classList.toggle("is-earned", earned);
            gap.classList.toggle("is-visible-tier", !!tier);
            /*
             * Sticky-right: only keep gaps between two visible rows
             * (after a filled slot, before the next shown tick).
             */
            if (stickyRight) {
              /* Only tier chips between visible rows; skip plain spacers */
              var showGap = !!tier && min <= count + 1 && min > 1;
              gap.hidden = !showGap;
              gap.classList.toggle("is-sidebar-gap", showGap);
            } else {
              gap.hidden = false;
              gap.classList.remove("is-sidebar-gap");
            }
            if (pctEl && tier) {
              pctEl.hidden = false;
              pctEl.textContent = tier.discount_pct + "%";
            }
          });
        }

        if (this.els.bar) {
          var fillPct = 0;
          if (count >= max && max > 0) {
            fillPct = 100;
          } else if (count > 0 && this.els.ticks) {
            var activeTick = this.els.ticks.querySelectorAll(".byob-tick")[count - 1];
            var activeDot = activeTick && activeTick.querySelector(".byob-tick__dot");
            var track = this.els.bar.parentElement;
            if (activeDot && track) {
              var trackRect = track.getBoundingClientRect();
              var dotRect = activeDot.getBoundingClientRect();
              if (trackRect.width > 0) {
                fillPct =
                  ((dotRect.left + dotRect.width / 2 - trackRect.left) / trackRect.width) * 100;
              }
            } else {
              fillPct = ((count - 0.5) / max) * 100;
            }
          }
          this.els.bar.style.width = Math.min(100, Math.max(0, fillPct)) + "%";
        }

        var subtotal = 0;
        items.forEach(function (it) {
          subtotal += it.price;
        });
        var discounted = Math.round(subtotal * (1 - pct / 100));
        var savingsText =
          pct > 0 && count > 0 ? "Saving " + pct + "%" : "";

        if (this.els.total) {
          if (!count) {
            this.els.total.innerHTML =
              '<span class="byob-cart-bar__current is-empty">—</span>';
          } else if (pct > 0) {
            this.els.total.innerHTML =
              '<span class="byob-cart-bar__current is-discounted">' +
              formatMoney(discounted, fmt) +
              "</span>" +
              '<span class="byob-cart-bar__compare">' +
              formatMoney(subtotal, fmt) +
              "</span>";
          } else {
            this.els.total.innerHTML =
              '<span class="byob-cart-bar__current">' + formatMoney(subtotal, fmt) + "</span>";
          }
        }
        if (this.els.savings) {
          if (savingsText) {
            this.els.savings.hidden = false;
            this.els.savings.textContent = savingsText;
            this.els.savings.title =
              "You save " + formatMoney(subtotal - discounted, fmt);
          } else {
            this.els.savings.hidden = true;
            this.els.savings.textContent = "";
            this.els.savings.removeAttribute("title");
          }
        }

        /* CTA enabled with selection; product adds blocked at max via syncCard/addCard */
        if (this.els.addBtn) {
          if (this.isStickyRightDesktop()) {
            var remaining = Math.max(0, max - count);
            if (count === 0) {
              this.els.addBtn.disabled = true;
              this.els.addBtn.textContent = "ADD " + max + " MORE ITEMS";
            } else if (remaining > 0) {
              this.els.addBtn.disabled = false;
              this.els.addBtn.textContent = this.addButtonText;
            } else {
              this.els.addBtn.disabled = false;
              this.els.addBtn.textContent = this.addButtonText;
            }
          } else {
            this.els.addBtn.disabled = count === 0;
            this.els.addBtn.textContent = this.addButtonText;
          }
        }

        this.renderCartDetails(pct, fmt);

        if (this.els.cartBar) {
          if (this.isStickyRightDesktop()) {
            this.els.cartBar.hidden = false;
            this.els.cartBar.classList.toggle("is-empty-bundle", count === 0);
            if (count === 0) {
              this.collapseCartDetails();
            }
          } else {
            this.els.cartBar.hidden = count === 0;
            this.els.cartBar.classList.remove("is-empty-bundle");
            if (count === 0) {
              this.collapseCartDetails();
            }
          }
        }
        if (this.els.emptyCallout) {
          this.els.emptyCallout.hidden = count > 0;
        }
        this._lastSelectionCount = count;
        this.syncProgressScrollUi();
        this.updateBottomPad();
      }

      scrollProgressSlots(direction) {
        var scroller = this.els.scroller;
        if (!scroller) return;
        var page = this.getProgressPageStep();
        scroller.scrollBy({ left: direction * page, behavior: "smooth" });
      }

      getProgressVisibleSlots() {
        /* Sticky-right desktop uses a vertical stack — no paging window. */
        if (this.isStickyRightDesktop()) return this.bundleMax;
        var desktopVisible = this.progressVisibleSlots || 6;
        /* Below 1200px: slider. At 1200px+: fit full bundle in one row */
        if (typeof window.matchMedia === "function" && window.matchMedia("(max-width: 1199px)").matches) {
          if (this.bundleMax > 6) return 5;
          return Math.min(desktopVisible, this.bundleMax);
        }
        return this.bundleMax;
      }

      isStickyRightDesktop() {
        return (
          this.progressAnchor === "right" &&
          (typeof window.matchMedia !== "function" ||
            window.matchMedia("(min-width: 1000px)").matches)
        );
      }

      getProgressPageStep() {
        var scroller = this.els.scroller;
        if (!scroller) return 120;
        var visible = this.getProgressVisibleSlots();
        var bubble = parseFloat(
          getComputedStyle(this.els.progress || this).getPropertyValue("--byob-bubble-size")
        );
        var tierGap = parseFloat(
          getComputedStyle(this.els.progress || this).getPropertyValue("--byob-tier-gap")
        );
        var gap = parseFloat(
          getComputedStyle(this.els.progress || this).getPropertyValue("--byob-tick-gap")
        );
        if (isNaN(bubble) || bubble <= 0) bubble = 44;
        if (isNaN(tierGap) || tierGap <= 0) tierGap = !isNaN(gap) && gap > 0 ? gap : 38;
        /* One page ≈ N bubbles + (N - 1) tier-width gaps */
        var step = bubble * visible + tierGap * Math.max(0, visible - 1);
        return Math.max(scroller.clientWidth * 0.85, step);
      }

      /**
       * ≥1200px: shrink bubbles/gaps so the full bundle (up to 10) fits in one row.
       */
      sizeDesktopProgressFit() {
        var progress = this.els.progress;
        var scroller = this.els.scroller;
        if (!progress || !scroller) return;

        /* Right sidebar stacks vertically — skip horizontal fit sizing. */
        if (this.isStickyRightDesktop()) {
          progress.classList.remove("byob-progress--desktop-fit");
          progress.classList.remove("byob-progress--mobile-paged");
          progress.style.removeProperty("--byob-bubble-size");
          progress.style.removeProperty("--byob-tick-gap");
          progress.style.removeProperty("--byob-tier-gap");
          return;
        }

        var isWideDesktop =
          typeof window.matchMedia !== "function" || window.matchMedia("(min-width: 1200px)").matches;
        if (!isWideDesktop) {
          progress.classList.remove("byob-progress--desktop-fit");
          /* Compact / slider path owns these vars when paged */
          if (!(this.bundleMax > 6 && window.matchMedia("(max-width: 1199px)").matches)) {
            progress.style.removeProperty("--byob-bubble-size");
            progress.style.removeProperty("--byob-tick-gap");
            progress.style.removeProperty("--byob-tier-gap");
          }
          return;
        }

        var max = this.bundleMax || 6;
        progress.classList.add("byob-progress--desktop-fit");

        if (max <= 6) {
          progress.style.removeProperty("--byob-bubble-size");
          progress.style.removeProperty("--byob-tick-gap");
          progress.style.removeProperty("--byob-tier-gap");
          return;
        }

        var width = scroller.clientWidth;
        if (width < 200) return;

        /* Leave room for % badges between slots (e.g. "45%") */
        var tierGap = max >= 9 ? 40 : 44;
        var bubble = Math.floor((width - tierGap * (max - 1)) / max);
        bubble = Math.max(48, Math.min(72, bubble));

        /* If still too wide, tighten gaps a bit */
        var total = bubble * max + tierGap * (max - 1);
        if (total > width) {
          tierGap = Math.max(34, Math.floor((width - bubble * max) / Math.max(1, max - 1)));
        }

        progress.style.setProperty("--byob-bubble-size", bubble + "px");
        progress.style.setProperty("--byob-tick-gap", "8px");
        progress.style.setProperty("--byob-tier-gap", tierGap + "px");
      }

      /**
       * Below 1200px with bundle max > 6: size bubbles so ~5 fit per slide.
       */
      sizeMobileProgressPage() {
        var progress = this.els.progress;
        var scroller = this.els.scroller;
        if (!progress || !scroller) return;

        if (this.isStickyRightDesktop()) {
          progress.classList.remove("byob-progress--mobile-paged");
          return;
        }

        var isCompact =
          typeof window.matchMedia === "function" && window.matchMedia("(max-width: 1199px)").matches;
        var paged = isCompact && this.bundleMax > 6;
        progress.classList.toggle("byob-progress--mobile-paged", paged);

        if (!paged) {
          if (!progress.classList.contains("byob-progress--desktop-fit")) {
            progress.style.removeProperty("--byob-bubble-size");
            progress.style.removeProperty("--byob-tick-gap");
            progress.style.removeProperty("--byob-tier-gap");
          }
          return;
        }

        progress.classList.remove("byob-progress--desktop-fit");

        var visible = 5;
        var plainGap = 10;
        var tierGap = 38;
        var width = scroller.clientWidth;
        if (width < 120) return;

        /*
         * First page has ~4 gaps between 5 ticks; most are tier gaps.
         * Budget for tier-width gaps so % badges keep left/right padding.
         */
        var bubble = Math.floor((width - tierGap * (visible - 1)) / visible);
        bubble = Math.max(36, Math.min(52, bubble));
        progress.style.setProperty("--byob-bubble-size", bubble + "px");
        progress.style.setProperty("--byob-tick-gap", plainGap + "px");
        progress.style.setProperty("--byob-tier-gap", tierGap + "px");
      }

      syncProgressScrollUi() {
        var scroller = this.els.scroller;
        if (!scroller) return;

        if (this.isStickyRightDesktop()) {
          scroller.classList.remove("is-scrollable");
          scroller.scrollLeft = 0;
          this.sizeDesktopProgressFit();
          this.sizeMobileProgressPage();
          if (this.els.scrollPrev) this.els.scrollPrev.hidden = true;
          if (this.els.scrollNext) this.els.scrollNext.hidden = true;
          return;
        }

        var visible = this.getProgressVisibleSlots();
        var shouldPage = this.bundleMax > visible;
        scroller.classList.toggle("is-scrollable", shouldPage);
        this.sizeDesktopProgressFit();
        this.sizeMobileProgressPage();

        /* Re-measure after class / size toggle (layout may change) */
        var maxScroll = scroller.scrollWidth - scroller.clientWidth;
        var canScroll = maxScroll > 4;
        var atStart = scroller.scrollLeft <= 4;
        var atEnd = scroller.scrollLeft >= maxScroll - 4;
        if (this.els.scrollPrev) {
          this.els.scrollPrev.hidden = !canScroll || atStart;
        }
        if (this.els.scrollNext) {
          this.els.scrollNext.hidden = !canScroll || atEnd;
        }
      }

      updateBottomPad() {
        var anchor = this.progressAnchor;
        var needsPad = anchor === "bottom";
        /* Sticky-right falls back to fixed bottom on mobile */
        if (
          anchor === "right" &&
          typeof window.matchMedia === "function" &&
          window.matchMedia("(max-width: 999px)").matches
        ) {
          needsPad = true;
        }
        if (!needsPad || !this.els.progress) {
          this.style.removeProperty("--byob-progress-pad");
          this.syncChatButtonOffset(0);
          return;
        }
        var h = Math.ceil(this.els.progress.getBoundingClientRect().height || 0);
        if (h > 0) {
          this.style.setProperty("--byob-progress-pad", h + 16 + "px");
          this.syncChatButtonOffset(h + 12);
        } else {
          this.syncChatButtonOffset(0);
        }
      }

      /**
       * Keep Gorgias / #chat-button clear of the fixed bottom BYOB bar.
       */
      syncChatButtonOffset(offsetPx) {
        var offset = Math.max(0, parseInt(offsetPx, 10) || 0);
        var root = document.documentElement;
        if (root) {
          if (offset > 0) {
            root.style.setProperty("--byob-chat-offset", offset + "px");
            root.classList.add("byob-has-bottom-bar");
          } else {
            root.style.removeProperty("--byob-chat-offset");
            root.classList.remove("byob-has-bottom-bar");
          }
        }

        var chat = document.getElementById("chat-button");
        if (chat) {
          if (offset > 0) {
            chat.style.setProperty("bottom", offset + "px", "important");
            chat.style.setProperty("margin-bottom", "0", "important");
          } else {
            chat.style.removeProperty("bottom");
            chat.style.removeProperty("margin-bottom");
          }
        }

        if (typeof window.GorgiasChat !== "undefined" && window.GorgiasChat.setPosition) {
          try {
            window.GorgiasChat.setPosition({
              offsetY: offset,
              offsetX: 0,
              alignment: "bottom-right"
            });
          } catch (e) {}
        }

        /* Chat widget often mounts after BYOB — re-apply once it appears */
        if (
          offset > 0 &&
          !this._chatOffsetObserver &&
          !document.getElementById("chat-button") &&
          typeof window.GorgiasChat === "undefined"
        ) {
          var self = this;
          this._chatOffsetObserver = new MutationObserver(function () {
            if (document.getElementById("chat-button") || window.GorgiasChat) {
              if (self._chatOffsetObserver) {
                self._chatOffsetObserver.disconnect();
                self._chatOffsetObserver = null;
              }
              self.updateBottomPad();
            }
          });
          this._chatOffsetObserver.observe(document.body, { childList: true, subtree: true });
          window.setTimeout(function () {
            if (self._chatOffsetObserver) {
              self._chatOffsetObserver.disconnect();
              self._chatOffsetObserver = null;
            }
          }, 15000);
        }
      }

      expandCartDetails() {
        if (this.els.cartPanel) {
          this.els.cartPanel.classList.add("is-expanded");
        }
        if (this.els.cartToggle) {
          this.els.cartToggle.setAttribute("aria-expanded", "true");
        }
        var details =
          this.els.cartDetails || this.querySelector("[data-byob-cart-details]");
        this.els.cartDetails = details;
        if (details) {
          details.hidden = false;
          details.removeAttribute("hidden");
        }
        if (this.els.cartToggleLabel) {
          this.els.cartToggleLabel.textContent = "Hide details";
        }
        /* Ensure list is populated when opening */
        this.renderCartDetails(this.currentDiscountPct(), this.moneyFormat);
      }

      collapseCartDetails() {
        if (this.els.cartPanel) {
          this.els.cartPanel.classList.remove("is-expanded");
        }
        if (this.els.cartToggle) {
          this.els.cartToggle.setAttribute("aria-expanded", "false");
        }
        var details =
          this.els.cartDetails || this.querySelector("[data-byob-cart-details]");
        this.els.cartDetails = details;
        if (details) {
          details.hidden = true;
          details.setAttribute("hidden", "");
        }
        if (this.els.cartToggleLabel) {
          this.els.cartToggleLabel.textContent = "See details";
        }
      }

      /**
       * Render selected products as compact horizontal rows (image left, info right).
       */
      renderCartDetails(pct, fmt) {
        var list =
          this.els.cartList || this.querySelector("[data-byob-cart-list]");
        this.els.cartList = list;
        if (!list) return;

        var discount = pct || 0;
        var moneyFmt = fmt || this.moneyFormat;
        var html = "";
        var items = [];

        this.selection.forEach(function (item) {
          items.push(item);
        });

        for (var i = 0; i < items.length; i++) {
          var item = items[i];
          var qty = item.qty || 1;
          var unit = item.price || 0;
          var line = unit * qty;
          var discountedLine = discount > 0 ? Math.round(line * (1 - discount / 100)) : line;
          var title = item.title || "Product";
          var img = item.image
            ? '<img src="' +
              escapeAttr(item.image) +
              '" alt="' +
              escapeAttr(title) +
              '" loading="lazy" width="56" height="56">'
            : '<span class="byob-cart-item__media-placeholder" aria-hidden="true"></span>';
          var priceHtml = "";
          if (this.priceDisplay !== "regular") {
            priceHtml =
              discount > 0
                ? '<span class="byob-cart-item__price is-discounted">' +
                  formatMoney(discountedLine, moneyFmt) +
                  "</span>" +
                  '<span class="byob-cart-item__compare">' +
                  formatMoney(line, moneyFmt) +
                  "</span>"
                : '<span class="byob-cart-item__price">' + formatMoney(line, moneyFmt) + "</span>";
          }

          html +=
            '<li class="byob-cart-item" data-variant-id="' +
            escapeAttr(item.variantId) +
            '">' +
            '<div class="byob-cart-item__media">' +
            img +
            "</div>" +
            '<div class="byob-cart-item__info">' +
            '<p class="byob-cart-item__title">' +
            escapeHtml(title) +
            "</p>" +
            '<div class="byob-cart-item__meta">' +
            (qty > 1 ? '<span class="byob-cart-item__qty">×' + qty + "</span>" : "") +
            priceHtml +
            "</div>" +
            "</div>" +
            '<button type="button" class="byob-cart-item__remove" data-byob-cart-remove="' +
            escapeAttr(item.variantId) +
            '" aria-label="Remove ' +
            escapeAttr(title) +
            ' from bundle"><span aria-hidden="true">&times;</span></button>' +
            "</li>";
        }

        list.innerHTML = html;
      }

      updateAllPrices() {
        var pct = this.currentDiscountPct();
        var fmt = this.moneyFormat;
        var priceDisplay = this.priceDisplay || "dynamic";
        var cards = this.querySelectorAll("byob-product-card");
        Array.prototype.forEach.call(cards, function (card) {
          card.applyDiscount(pct, fmt, priceDisplay);
        });
      }

      /* ---------- Filters + sort view ---------- */
      setFilter(value) {
        this.activeFilter = value || "";
        this.currentPage = 1;
        if (this.els.filters) {
          var btns = this.els.filters.querySelectorAll(".byob-filter");
          Array.prototype.forEach.call(btns, function (b) {
            var active = (b.dataset.byobFilter || "") === value;
            b.classList.toggle("is-active", active);
            b.setAttribute("aria-pressed", active ? "true" : "false");
          });
        }
        this.refreshCatalogView();
      }

      /**
       * Show/hide the product-grid overlay while filter/sort requests run.
       */
      setCatalogLoading(isLoading, label) {
        var loading = !!isLoading;
        if (this.els.gridShell) {
          this.els.gridShell.classList.toggle("is-loading", loading);
        }
        if (this.els.toolbar) {
          this.els.toolbar.classList.toggle("is-loading", loading);
        }
        if (this.els.sort) {
          this.els.sort.disabled = loading;
        }
        if (this.els.filters) {
          var btns = this.els.filters.querySelectorAll(".byob-filter");
          Array.prototype.forEach.call(btns, function (btn) {
            btn.disabled = loading;
          });
        }
        if (this.els.status) {
          this.els.status.hidden = !loading;
          this.els.status.setAttribute("aria-hidden", loading ? "false" : "true");
          var labelEl = this.els.status.querySelector(".byob-grid-overlay__label");
          if (labelEl && label) labelEl.textContent = label;
        }
      }

      /**
       * Apply filter + sort to the product grid.
       *
       * Filter click always fetches that shape from the active collection
       * (tagged collection ?view=byob-data), then paginates the result.
       * Sort with no filter stays on the local catalog.
       */
      refreshCatalogView() {
        var self = this;
        var filterDef = this.getActiveFilterDef();

        if (!filterDef) {
          this.filterRequestId += 1;
          this.apiViewProducts = null;
          this.resetRemotePaging("");
          this.setCatalogLoading(false);
          this.applyView();
          return;
        }

        var tagHandle = collectionTagHandle(filterDef);
        var cacheKey = this.activeCatalog + "|" + filterDef.value;
        if (this._filteredCache && this._filteredCache.key === cacheKey) {
          this.filterRequestId += 1;
          this.apiViewProducts = this.sortProductList(this._filteredCache.products.slice());
          this._remoteTagHandle = this._filteredCache.tagHandle || tagHandle;
          this._remotePage = this._filteredCache.remotePage || 1;
          this._remotePages = this._filteredCache.remotePages || 0;
          this._remoteCount = this._filteredCache.remoteCount || this.apiViewProducts.length;
          this.currentPage = 1;
          this.setCatalogLoading(false);
          this.applyView();
          return;
        }

        var reqId = ++this.filterRequestId;
        this.currentPage = 1;
        this.resetRemotePaging(tagHandle);
        this.setCatalogLoading(true, "Filtering products…");

        this.fetchLiquidPage(1, { tagHandle: tagHandle })
          .then(function (result) {
            if (reqId !== self.filterRequestId) return;
            var products = (result && result.products) || [];
            if (!products.length) {
              return self.fetchFilteredProducts().then(function (fallback) {
                return {
                  products: (fallback && fallback.products) || [],
                  page: 1,
                  pages: 1,
                  count: ((fallback && fallback.products) || []).length
                };
              });
            }
            return result;
          })
          .then(function (result) {
            if (reqId !== self.filterRequestId) return;
            var products = self.sortProductList((result && result.products) || []);
            self.apiViewProducts = products;
            self._remotePage = result && result.page ? result.page : 1;
            self._remotePages = result && result.pages ? result.pages : 1;
            self._remoteCount = result && result.count ? result.count : products.length;
            self._filteredCache = {
              key: cacheKey,
              products: products.slice(),
              tagHandle: tagHandle,
              remotePage: self._remotePage,
              remotePages: self._remotePages,
              remoteCount: self._remoteCount
            };
            self.currentPage = 1;
            self.applyView();
          })
          .catch(function (err) {
            if (reqId !== self.filterRequestId) return;
            if (window.console) console.warn("[BYOB] Filtered catalog fetch failed:", err);
            self.apiViewProducts = self.sortProductList(
              (self.dataset_products || []).filter(function (p) {
                return matchesNailShapeFilter(p, self.activeFilter);
              })
            );
            self.applyView();
          })
          .then(function () {
            if (reqId !== self.filterRequestId) return;
            self.setCatalogLoading(false);
          });
      }

      /** Fetch collection/products for the active filter + sort combination. */
      fetchCatalogForView() {
        var sort = resolveSortOption(this.activeSort);
        var filterDef = this.getActiveFilterDef();

        if (filterDef) {
          return this.fetchFilteredProducts(sort);
        }

        return this.fetchCollectionProducts({
          sortKey: sort.collectionSortKey,
          reverse: sort.reverse
        }).then(function (products) {
          return { ok: true, products: products || [], source: "collection.sort" };
        });
      }

      sortProductList(list) {
        var sort = this.activeSort;
        var items = (list || []).slice();
        items.sort(function (a, b) {
          switch (sort) {
            case "PRICE:asc":
              return a.price - b.price;
            case "PRICE:desc":
              return b.price - a.price;
            case "TITLE":
              return a.title.localeCompare(b.title);
            case "CREATED":
              return String(b.createdAt || "").localeCompare(String(a.createdAt || ""));
            default:
              return a.bestSellingIndex - b.bestSellingIndex;
          }
        });
        return items;
      }

      getVisibleProducts() {
        var filter = this.activeFilter;
        var usingApiView = this.apiViewProducts != null;
        var source = usingApiView ? this.apiViewProducts : this.dataset_products;

        if (usingApiView) {
          return this.sortProductList(source);
        }

        var list = (source || []).filter(function (p) {
          return matchesNailShapeFilter(p, filter);
        });
        return this.sortProductList(list);
      }

      getPageParts(current, total) {
        if (total <= 1) return [];
        var parts = [];
        var push = function (title, page) {
          parts.push({ title: title, page: page || null });
        };
        if (total <= 7) {
          for (var i = 1; i <= total; i++) push(String(i), i);
          return parts;
        }
        push("1", 1);
        if (current > 3) push("…");
        var start = Math.max(2, current - 1);
        var end = Math.min(total - 1, current + 1);
        if (current <= 3) {
          start = 2;
          end = 4;
        }
        if (current >= total - 2) {
          start = total - 3;
          end = total - 1;
        }
        for (var p = start; p <= end; p++) push(String(p), p);
        if (current < total - 2) push("…");
        push(String(total), total);
        return parts;
      }

      renderPagination(totalItems) {
        if (this.enableLoadMore) {
          this.renderLoadMore(totalItems);
          if (this.els.pagination) {
            this.els.pagination.hidden = true;
            this.els.pagination.innerHTML = "";
          }
          return;
        }

        if (this.els.loadMore) {
          this.els.loadMore.hidden = true;
        }

        if (this.els.results) {
          if (totalItems === 0) {
            this.els.results.hidden = true;
            this.els.results.textContent = "";
          } else {
            var perPage = this.productsPerPage;
            var start = (this.currentPage - 1) * perPage + 1;
            var end = Math.min(totalItems, this.currentPage * perPage);
            this.els.results.hidden = false;
            this.els.results.textContent = "Showing " + start + "–" + end + " of " + totalItems;
          }
        }

        if (!this.els.pagination) return;
        var perPage = this.productsPerPage;
        var totalPages = Math.max(1, Math.ceil(totalItems / perPage));
        if (this.currentPage > totalPages) this.currentPage = totalPages;
        if (this.currentPage < 1) this.currentPage = 1;

        if (totalPages <= 1 || totalItems === 0) {
          this.els.pagination.hidden = true;
          this.els.pagination.innerHTML = "";
          return;
        }

        var current = this.currentPage;
        var iconLeft =
          '<svg aria-hidden="true" focusable="false" fill="none" width="11" viewBox="0 0 10 16"><path d="M8 1 1.5 8 8 15" stroke="currentColor" stroke-width="1.5"/></svg>';
        var iconRight =
          '<svg aria-hidden="true" focusable="false" fill="none" width="11" viewBox="0 0 10 16"><path d="m2 1 6.5 7L2 15" stroke="currentColor" stroke-width="1.5"/></svg>';
        var html = "";

        if (current > 1) {
          html +=
            '<button type="button" class="pagination__link h6" data-byob-page="' +
            (current - 1) +
            '" rel="prev" aria-label="Go to page ' +
            (current - 1) +
            '">' +
            iconLeft +
            "</button>";
        }

        this.getPageParts(current, totalPages).forEach(function (part) {
          if (part.page == null) {
            html +=
              '<span class="pagination__link pagination__link--disabled h6">' +
              escapeHtml(part.title) +
              "</span>";
            return;
          }
          if (part.page === current) {
            html +=
              '<span class="pagination__link pagination__link--disabled h6" aria-current="page">' +
              escapeHtml(part.title) +
              "</span>";
            return;
          }
          html +=
            '<button type="button" class="pagination__link h6" data-byob-page="' +
            part.page +
            '" aria-label="Go to page ' +
            part.page +
            '">' +
            escapeHtml(part.title) +
            "</button>";
        });

        if (current < totalPages) {
          html +=
            '<button type="button" class="pagination__link h6" data-byob-page="' +
            (current + 1) +
            '" rel="next" aria-label="Go to page ' +
            (current + 1) +
            '">' +
            iconRight +
            "</button>";
        }

        this.els.pagination.innerHTML = html;
        this.els.pagination.hidden = false;
      }

      renderLoadMore(totalItems) {
        if (this.els.results) {
          this.els.results.hidden = true;
          this.els.results.textContent = "";
        }
        if (!this.els.loadMore) return;

        var perPage = this.productsPerPage;
        var displayTotal = this.remoteDisplayTotal(totalItems);
        var shown = Math.min(totalItems || 0, this.currentPage * perPage);
        var hasMore = shown < displayTotal && displayTotal > 0;

        if (this.els.loadMoreCount) {
          if (this.showLoadMoreCount && displayTotal > 0) {
            this.els.loadMoreCount.hidden = false;
            this.els.loadMoreCount.textContent =
              "Showing " + shown + " of " + displayTotal + " products";
          } else {
            this.els.loadMoreCount.hidden = true;
            this.els.loadMoreCount.textContent = "";
          }
        }

        if (this.els.loadMoreLabel) {
          this.els.loadMoreLabel.textContent = this.loadMoreButtonLabel;
        }

        if (this.els.loadMoreButton) {
          this.els.loadMoreButton.hidden = !hasMore;
          this.els.loadMoreButton.disabled = !hasMore;
        }

        this.els.loadMore.hidden = displayTotal === 0 || (!hasMore && !this.showLoadMoreCount);
        this.els.loadMore.classList.remove("is-loading");
      }

      loadMoreProducts() {
        if (!this.enableLoadMore || !this.els.grid || this._loadMoreHydrating) return;
        var products = this.getVisibleProducts();
        var perPage = this.productsPerPage;
        var nextStart = this.currentPage * perPage;
        var remaining = Math.max(0, products.length - nextStart);

        if (remaining >= perPage || (remaining > 0 && !this.hasMoreRemotePages())) {
          this.appendVisiblePage();
          return;
        }

        if (this.hasMoreRemotePages() || this._remotePage === 0) {
          this.fetchNextRemotePage();
          return;
        }

        this.renderLoadMore(products.length);
      }

      appendVisiblePage() {
        var products = this.getVisibleProducts();
        var perPage = this.productsPerPage;
        this.currentPage += 1;
        var start = (this.currentPage - 1) * perPage;
        var pageProducts = products.slice(start, start + perPage);
        if (!pageProducts.length) {
          this.renderLoadMore(products.length);
          return;
        }
        if (this.els.loadMore) this.els.loadMore.classList.add("is-loading");
        if (this.els.loadMoreButton) this.els.loadMoreButton.disabled = true;
        var cardOpts = {
          isModern: isModernCardStyle(this.config.productCardStyle || "modern"),
          align: this.config.cardContentAlignment || "left",
          atcStyle: this.atcStyle || "button"
        };
        var self = this;
        this.ensureReviews(pageProducts).then(function () {
          var frag = document.createDocumentFragment();
          pageProducts.forEach(function (p) {
            frag.appendChild(ByobProductCard.build(p, cardOpts));
          });
          self.els.grid.appendChild(frag);
          self.renderLoadMore(self.getVisibleProducts().length);
          self.syncAllCards();
          self.updateAllPrices();
        });
      }

      appendRemoteProducts(incoming) {
        this.mergeProducts(incoming || []);
        if (!this.apiViewProducts) return;
        var seen = {};
        this.apiViewProducts.forEach(function (p) {
          if (p && p.variantId) seen[p.variantId] = true;
        });
        var self = this;
        (incoming || []).forEach(function (p) {
          if (!p || !p.variantId || seen[p.variantId]) return;
          seen[p.variantId] = true;
          self.apiViewProducts.push(p);
        });
      }

      rememberFilteredPage() {
        if (!this.activeFilter || !this.apiViewProducts) return;
        var filterDef = this.getActiveFilterDef();
        if (!filterDef) return;
        this._filteredCache = {
          key: this.activeCatalog + "|" + filterDef.value,
          products: this.apiViewProducts.slice(),
          tagHandle: this._remoteTagHandle,
          remotePage: this._remotePage,
          remotePages: this._remotePages,
          remoteCount: this._remoteCount
        };
      }

      fetchNextRemotePage() {
        if (this._loadMoreHydrating) return;
        var self = this;
        var nextStart = this.currentPage * this.productsPerPage;
        this._loadMoreHydrating = true;
        if (this.els.loadMore) this.els.loadMore.classList.add("is-loading");
        if (this.els.loadMoreButton) this.els.loadMoreButton.disabled = true;

        var nextPage = (this._remotePage || 0) + 1;
        this.fetchLiquidPage(nextPage, { tagHandle: this._remoteTagHandle })
          .then(function (result) {
            var incoming = (result && result.products) || [];
            self._remotePage = (result && result.page) || nextPage;
            if (result && result.pages) self._remotePages = result.pages;
            if (result && result.count) self._remoteCount = result.count;
            if (!incoming.length) {
              self._remotePages = self._remotePage;
              return [];
            }
            self.appendRemoteProducts(incoming);
            self.rememberFilteredPage();
            return self.ensureReviews(incoming);
          })
          .catch(function (err) {
            if (window.console) console.warn("[BYOB] Load more page fetch failed:", err);
          })
          .then(function () {
            self._loadMoreHydrating = false;
            if (self.activeCatalog === "accessories") {
              self.accessoriesCatalogCache = self.dataset_products.slice();
            } else {
              self.nailsCatalogCache = self.dataset_products.slice();
            }
            var list = self.getVisibleProducts();
            if (list.length > nextStart) self.appendVisiblePage();
            else self.renderLoadMore(list.length);
          });
      }

      loadFirstRemotePage() {
        var self = this;
        this.resetRemotePaging(this._remoteTagHandle || "");
        this.currentPage = 1;
        this.setCatalogLoading(true, "Loading products…");
        this.fetchLiquidPage(1, { tagHandle: this._remoteTagHandle })
          .then(function (result) {
            var products = (result && result.products) || [];
            self._remotePage = (result && result.page) || 1;
            if (result && result.pages) self._remotePages = result.pages;
            if (result && result.count) self._remoteCount = result.count;
            self.dataset_products = [];
            self.mergeProducts(products);
            self.apiViewProducts = self.activeFilter ? self.sortProductList(products) : null;
            self.applyView();
          })
          .catch(function (err) {
            if (window.console) console.warn("[BYOB] First catalog page failed:", err);
            self.applyView();
          })
          .then(function () {
            self.setCatalogLoading(false);
          });
      }

      ensureReviews(products) {
        var missing = (products || []).filter(function (p) {
          return !(parseFloat(p.rating) || parseInt(p.ratingCount, 10));
        });
        if (!missing.length || !this.config.token) return Promise.resolve(products || []);
        return this.enrichMetafieldsFromStorefront(missing).then(function () {
          return products;
        });
      }

      applyView(opts) {
        opts = opts || {};
        if (!this.els.grid) return;
        var products = this.getVisibleProducts();
        if (products.length === 0) {
          this.els.grid.innerHTML = '<p class="byob-grid__empty">No products match this filter.</p>';
          this.renderPagination(0);
          return;
        }

        var perPage = this.productsPerPage;
        var totalPages = Math.max(1, Math.ceil(products.length / perPage));
        if (this.currentPage > totalPages) this.currentPage = totalPages;

        var pageProducts;
        if (this.enableLoadMore) {
          /* Load-more mode always rebuilds pages 1..current so filters/sort stay consistent. */
          var end = this.currentPage * perPage;
          pageProducts = products.slice(0, end);
        } else {
          var start = (this.currentPage - 1) * perPage;
          pageProducts = products.slice(start, start + perPage);
        }

        var cardOpts = {
          isModern: isModernCardStyle(this.config.productCardStyle || "modern"),
          align: this.config.cardContentAlignment || "left",
          atcStyle: this.atcStyle || "button"
        };
        var self = this;
        this.ensureReviews(pageProducts).then(function () {
          var frag = document.createDocumentFragment();
          pageProducts.forEach(function (p) {
            frag.appendChild(ByobProductCard.build(p, cardOpts));
          });
          self.els.grid.innerHTML = "";
          self.els.grid.appendChild(frag);
          self.renderPagination(products.length);
          self.syncAllCards();
          self.updateAllPrices();

          if (opts.scroll) {
            var target = self.els.grid;
            if (target && typeof target.scrollIntoView === "function") {
              target.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }
        });
      }

      /* ---------- Catalog hydration ---------- */
      expectedCatalogCount() {
        var cfg = this.config || {};
        if (this.activeCatalog === "accessories") {
          return parseInt(cfg.accessoriesProductsCount, 10) || 0;
        }
        return parseInt(cfg.collectionProductsCount, 10) || 0;
      }

      uniqueProductCount(list) {
        var seen = {};
        var count = 0;
        (list || []).forEach(function (product) {
          if (!product || !product.variantId || seen[product.variantId]) return;
          seen[product.variantId] = true;
          count += 1;
        });
        return count;
      }

      catalogLooksIncomplete(list) {
        if (this.activeFilter) return false;
        var count = this.uniqueProductCount(list);
        if (!count) return true;
        var expected = this.expectedCatalogCount();
        if (expected > 0 && count < expected) return true;
        if (expected <= 0 && count <= this.productsPerPage) return true;
        return false;
      }

      hydrateFromStorefront() {
        var cfg = this.config;
        var handle = this.getActiveCollectionHandle();
        if (!handle || this._hydrating) return;
        cfg.collectionHandle = handle;
        var self = this;
        var catalogAtStart = this.activeCatalog;
        this._hydrating = true;
        this.setCatalogLoading(true, "Loading products…");

        var liquidPromise = this.fetchAllProductsLiquid();
        var storefrontPromise = cfg.token
          ? this.fetchAllProducts().catch(function (err) {
              if (window.console) console.warn("[BYOB] Storefront catalog hydration failed:", err);
              return [];
            })
          : Promise.resolve([]);

        function applyCatalog(sources) {
          if (self.activeCatalog !== catalogAtStart) return false;
          var merged = [];
          sources.forEach(function (list) {
            if (list && list.length) merged = merged.concat(list);
          });
          if (!merged.length) return false;

          /*
           * Re-read SSR cards before merge so visible subtitles are captured
           * even when data-subtitle was empty (nails first paint only).
           */
          if (catalogAtStart === "nails") {
            var fromDom = self.readInitialProducts();
            if (fromDom.length) self.dataset_products = fromDom;
          }
          sources.forEach(function (list) {
            if (list && list.length) self.mergeProducts(list);
          });
          if (catalogAtStart === "accessories") {
            self.accessoriesCatalogCache = self.dataset_products.slice();
          } else {
            self.nailsCatalogCache = self.dataset_products.slice();
          }
          self.currentPage = 1;
          self.apiViewProducts = null;
          self.applyView();
          self.enrichSelectionFromCatalog();
          return true;
        }

        Promise.all([liquidPromise, storefrontPromise])
          .then(function (results) {
            var liquid = results[0] || [];
            var storefront = results[1] || [];
            if (!self.catalogLooksIncomplete(liquid.concat(storefront))) {
              applyCatalog([liquid, storefront]);
              return null;
            }
            return self.fetchAllProductsJson().then(function (jsonProducts) {
              applyCatalog([liquid, storefront, jsonProducts || []]);
              var catalog = self.dataset_products || [];
              if (!cfg.token || !catalog.length) return catalog;
              return self.enrichMetafieldsFromStorefront(catalog).then(function () {
                if (self.activeCatalog !== catalogAtStart) return catalog;
                if (self.currentPage <= 1) self.applyView();
                self.enrichSelectionFromCatalog();
                return catalog;
              });
            });
          })
          .catch(function (err) {
            if (window.console) console.warn("[BYOB] Catalog hydration failed:", err);
          })
          .then(function () {
            self._hydrating = false;
            self.setCatalogLoading(false);
            if (
              self.catalogLooksIncomplete(self.dataset_products) &&
              (self._hydrateAttempts || 0) < 2
            ) {
              self._hydrateAttempts = (self._hydrateAttempts || 0) + 1;
              setTimeout(function () {
                self.hydrateFromStorefront();
              }, 400 * self._hydrateAttempts);
            }
          });
      }

      fetchLiquidPage(pageNum, opts) {
        opts = opts || {};
        var handle = this.getActiveCollectionHandle() || (this.config && this.config.collectionHandle);
        if (!handle) {
          return Promise.resolve({ products: [], page: pageNum, pages: 0, count: 0 });
        }
        var tagHandle = opts.tagHandle ? String(opts.tagHandle).replace(/^\/+|\/+$/g, "") : "";
        var indexBase = this.uniqueProductCount(this.dataset_products);
        var url =
          ROOT +
          "collections/" +
          encodeURIComponent(handle) +
          (tagHandle ? "/" + encodeURIComponent(tagHandle) : "") +
          "?page=" +
          pageNum +
          "&view=byob-data";

        return fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
          .then(function (r) {
            if (!r.ok) throw new Error("byob-data " + r.status);
            return r.text();
          })
          .then(function (text) {
            var cleaned = String(text || "").trim();
            var start = cleaned.indexOf("{");
            var end = cleaned.lastIndexOf("}");
            if (start === -1 || end === -1) throw new Error("byob-data missing JSON");
            var json = JSON.parse(cleaned.slice(start, end + 1));
            if (!json || !Array.isArray(json.products) || typeof json.pages !== "number") {
              throw new Error("byob-data invalid payload");
            }
            var products = [];
            json.products.forEach(function (product) {
              var tags = Array.isArray(product.tags)
                ? product.tags.map(function (t) {
                    return String(t).toLowerCase();
                  })
                : String(product.tags || "")
                    .split(",")
                    .map(function (t) {
                      return t.trim().toLowerCase();
                    })
                    .filter(Boolean);
              if (tags.indexOf("hide") !== -1) return;
              if (!product.variantId) return;
              products.push({
                variantId: String(product.variantId),
                productId: String(product.productId),
                handle: product.handle,
                title: product.title,
                subtitle: product.subtitle || "",
                customLabel: product.customLabel || "",
                price: parseInt(product.price, 10) || 0,
                compareAtPrice: parseInt(product.compareAtPrice, 10) || 0,
                image: product.image || "",
                shape: String(product.shape || "").toLowerCase().trim() || shapeFromTags(tags),
                tags: tags,
                rating: parseFloat(product.rating) || 0,
                ratingCount: parseInt(product.ratingCount, 10) || 0,
                available: product.available !== false,
                createdAt: product.createdAt || null,
                bestSellingIndex: indexBase++
              });
            });
            return {
              products: products,
              page: json.page || pageNum,
              pages: json.pages || 1,
              count: typeof json.count === "number" ? json.count : products.length
            };
          });
      }

      fetchAllProductsLiquid(opts) {
        opts = opts || {};
        var cfg = this.config;
        var handle = this.getActiveCollectionHandle() || cfg.collectionHandle;
        if (!handle) return Promise.resolve([]);
        var tagHandle = opts.tagHandle ? String(opts.tagHandle).replace(/^\/+|\/+$/g, "") : "";
        var collected = [];
        var bestSellingIndex = 0;
        var maxPages = 40;
        var lastPageSignature = "";

        function normalizeTags(tags) {
          if (Array.isArray(tags)) {
            return tags.map(function (t) {
              return String(t).toLowerCase();
            });
          }
          return String(tags || "")
            .split(",")
            .map(function (t) {
              return t.trim().toLowerCase();
            })
            .filter(Boolean);
        }

        function page(pageNum) {
          var url =
            ROOT +
            "collections/" +
            encodeURIComponent(handle) +
            (tagHandle ? "/" + encodeURIComponent(tagHandle) : "") +
            "?page=" +
            pageNum +
            "&view=byob-data";
          return fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
            .then(function (r) {
              if (!r.ok) throw new Error("byob-data " + r.status);
              return r.text();
            })
            .then(function (text) {
              var cleaned = String(text || "").trim();
              var start = cleaned.indexOf("{");
              var end = cleaned.lastIndexOf("}");
              if (start === -1 || end === -1) throw new Error("byob-data missing JSON");
              var json = JSON.parse(cleaned.slice(start, end + 1));
              if (!json || !Array.isArray(json.products) || typeof json.pages !== "number") {
                throw new Error("byob-data invalid payload");
              }
              var products = json.products;
              var signature = products
                .map(function (product) {
                  return String(product && product.variantId ? product.variantId : "");
                })
                .join(",");
              if (signature && signature === lastPageSignature) {
                return collected;
              }
              lastPageSignature = signature;
              products.forEach(function (product) {
                var tags = normalizeTags(product.tags);
                if (tags.indexOf("hide") !== -1) return;
                collected.push({
                  variantId: String(product.variantId),
                  productId: String(product.productId),
                  handle: product.handle,
                  title: product.title,
                  subtitle: product.subtitle || "",
                  customLabel: product.customLabel || "",
                  price: parseInt(product.price, 10) || 0,
                  compareAtPrice: parseInt(product.compareAtPrice, 10) || 0,
                  image: product.image || "",
                  shape: String(product.shape || "").toLowerCase().trim() || shapeFromTags(tags),
                  tags: tags,
                  rating: parseFloat(product.rating) || 0,
                  ratingCount: parseInt(product.ratingCount, 10) || 0,
                  available: product.available !== false,
                  createdAt: product.createdAt || null,
                  bestSellingIndex: bestSellingIndex++
                });
              });
              var totalPages = json.pages || 1;
              if (pageNum < totalPages && pageNum < maxPages) {
                return page(pageNum + 1);
              }
              return collected;
            });
        }

        return page(1).catch(function (err) {
          if (window.console) console.warn("[BYOB] Liquid catalog hydration failed:", err);
          return collected;
        });
      }

      enrichMetafieldsFromStorefront(products) {
        var cfg = this.config;
        if (!cfg.token || !products || !products.length) return Promise.resolve(products || []);

        var version = cfg.apiVersion || "2025-01";
        var endpoint = ROOT + "api/" + version + "/graphql.json";
        var byProductId = {};
        products.forEach(function (p) {
          byProductId[String(p.productId)] = p;
        });
        var ids = Object.keys(byProductId).map(function (id) {
          return "gid://shopify/Product/" + id;
        });
        var chunkSize = 50;

        function parseOkendo(value) {
          var rating = 0;
          var count = 0;
          if (!value) return { rating: 0, count: 0 };
          try {
            var data = typeof value === "string" ? JSON.parse(value) : value;
            rating = parseFloat(data.reviewAverageValue) || 0;
            count = parseInt(data.reviewCount, 10) || 0;
          } catch (e) {}
          return { rating: rating, count: count };
        }

        function fetchChunk(start) {
          if (start >= ids.length) return Promise.resolve(products);
          var slice = ids.slice(start, start + chunkSize);
          var query =
            "query($ids:[ID!]!){nodes(ids:$ids){...on Product{id handle " +
            'subtitle:metafield(namespace:"global",key:"subtitle"){value} ' +
            'customLabel:metafield(namespace:"custom",key:"custom_label"){value} ' +
            'nailShape:metafield(namespace:"custom",key:"nail_shape"){value} ' +
            'okendo:metafield(namespace:"okendo",key:"summaryData"){value}}}}';

          return fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": cfg.token
            },
            body: JSON.stringify({ query: query, variables: { ids: slice } })
          })
            .then(function (r) {
              return r.json();
            })
            .then(function (json) {
              var nodes = (json && json.data && json.data.nodes) || [];
              nodes.forEach(function (node) {
                if (!node || !node.id) return;
                var id = numericId(node.id);
                var product = byProductId[String(id)];
                if (!product) return;
                if (node.subtitle && node.subtitle.value) product.subtitle = String(node.subtitle.value);
                if (node.customLabel && node.customLabel.value) {
                  product.customLabel = String(node.customLabel.value);
                }
                if (node.nailShape && node.nailShape.value) {
                  product.shape = String(node.nailShape.value).toLowerCase().trim();
                }
                var okendo = parseOkendo(node.okendo && node.okendo.value);
                if (okendo.count || okendo.rating) {
                  product.rating = okendo.rating;
                  product.ratingCount = okendo.count;
                }
              });
              return fetchChunk(start + chunkSize);
            })
            .catch(function (err) {
              if (window.console) console.warn("[BYOB] Metafield enrichment failed:", err);
              return products;
            });
        }

        return fetchChunk(0);
      }

      mergeProducts(incoming) {
        var byId = {};
        this.dataset_products.forEach(function (p) {
          byId[p.variantId] = p;
        });
        incoming.forEach(function (p) {
          if (!p || !p.variantId) return;
          var prev = byId[p.variantId];
          if (prev) {
            /* Never drop a known subtitle/label when incoming data omits it */
            if (!(p.subtitle && String(p.subtitle).trim()) && prev.subtitle) {
              p.subtitle = prev.subtitle;
            }
            if (!(p.customLabel && String(p.customLabel).trim()) && prev.customLabel) {
              p.customLabel = prev.customLabel;
            }
            if (!(parseFloat(p.rating) || parseInt(p.ratingCount, 10))) {
              if (prev.rating) p.rating = prev.rating;
              if (prev.ratingCount) p.ratingCount = prev.ratingCount;
            }
            if (!p.image && prev.image) p.image = prev.image;
            if (!p.shape && prev.shape) p.shape = prev.shape;
          }
          byId[p.variantId] = p;
        });
        this.dataset_products = Object.keys(byId).map(function (k) {
          return byId[k];
        });
      }

      fetchAllProductsJson() {
        var cfg = this.config;
        var handle = this.getActiveCollectionHandle() || cfg.collectionHandle;
        if (!handle) return Promise.resolve([]);
        var collected = [];
        var bestSellingIndex = 0;
        var maxPages = 40;

        function mapProduct(product) {
          var tagsRaw = product.tags || [];
          var tags = Array.isArray(tagsRaw)
            ? tagsRaw.map(function (t) {
                return String(t).toLowerCase();
              })
            : String(tagsRaw)
                .split(",")
                .map(function (t) {
                  return t.trim().toLowerCase();
                })
                .filter(Boolean);
          if (tags.indexOf("hide") !== -1) return null;
          var variant = product.variants && product.variants[0];
          if (!variant) return null;
          var shape = shapeFromTags(tags);
          var image =
            (product.images && product.images[0] && (product.images[0].src || product.images[0])) ||
            product.featured_image ||
            "";
          var priceCents =
            typeof variant.price === "string"
              ? Math.round(parseFloat(variant.price) * 100)
              : Math.round((parseFloat(variant.price) || 0) * 100);
          var compareAtCents = 0;
          if (variant.compare_at_price != null && variant.compare_at_price !== "") {
            compareAtCents =
              typeof variant.compare_at_price === "string"
                ? Math.round(parseFloat(variant.compare_at_price) * 100)
                : Math.round((parseFloat(variant.compare_at_price) || 0) * 100);
          }
          return {
            variantId: String(variant.id),
            productId: String(product.id),
            handle: product.handle,
            title: product.title,
            subtitle: "",
            customLabel: "",
            price: priceCents,
            compareAtPrice: compareAtCents,
            image: image,
            shape: shape,
            tags: tags,
            rating: 0,
            ratingCount: 0,
            available: variant.available !== false,
            createdAt: product.created_at || null,
            bestSellingIndex: bestSellingIndex++
          };
        }

        function page(pageNum) {
          var url =
            ROOT +
            "collections/" +
            encodeURIComponent(handle) +
            "/products.json?limit=250&page=" +
            pageNum;
          return fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
            .then(function (r) {
              if (!r.ok) throw new Error("products.json " + r.status);
              return r.json();
            })
            .then(function (json) {
              var products = (json && json.products) || [];
              products.forEach(function (product) {
                var mapped = mapProduct(product);
                if (mapped) collected.push(mapped);
              });
              if (products.length === 250 && pageNum < maxPages) {
                return page(pageNum + 1);
              }
              return collected;
            });
        }

        return page(1).catch(function (err) {
          if (window.console) console.warn("[BYOB] products.json hydration failed:", err);
          return collected;
        });
      }

      /**
       * Fetch only products in the active collection that match the nail-shape filter.
       * 1) Tagged collection + byob-data (has Okendo ratings)
       * 2) Storefront collection.products(filters: tag / metafield)
       * 3) products(query:) fallback, scoped to the BYOB catalog when possible
       */
      fetchFilteredProducts(sortOpts) {
        var self = this;
        var filterDef = this.getActiveFilterDef();
        var sort = sortOpts || resolveSortOption(this.activeSort);
        if (!filterDef) {
          return Promise.resolve({ ok: true, products: (this.dataset_products || []).slice() });
        }

        function matchedFrom(products, source) {
          var matched = (products || []).filter(function (p) {
            return matchesNailShapeFilter(p, filterDef.value);
          });
          return { ok: true, products: matched, source: source };
        }

        function fromCollection(filters, source) {
          return self
            .fetchCollectionProducts({
              filters: filters,
              sortKey: sort.collectionSortKey,
              reverse: sort.reverse
            })
            .then(function (products) {
              return matchedFrom(products, source);
            });
        }

        function fromStorefront() {
          return fromCollection(buildCollectionProductFilters(filterDef), "collection.tag")
            .then(function (tagResult) {
              if (tagResult.products.length > 0) return tagResult;

              var metafieldFilters = [
                {
                  productMetafield: {
                    namespace: "custom",
                    key: "nail_shape",
                    value: filterDef.value
                  }
                }
              ];

              return fromCollection(metafieldFilters, "collection.metafield").then(function (mfResult) {
                if (mfResult.products.length > 0) return mfResult;

                return self.fetchShopProductsQuery(filterDef, sort).then(function (shopProducts) {
                  var scoped = self.scopeProductsToCatalog(shopProducts);
                  return matchedFrom(scoped.length ? scoped : shopProducts, "products.query");
                });
              });
            })
            .catch(function (err) {
              if (window.console) console.warn("[BYOB] collection filter query failed:", err);
              return self.fetchShopProductsQuery(filterDef, sort).then(function (shopProducts) {
                var scoped = self.scopeProductsToCatalog(shopProducts);
                return matchedFrom(scoped.length ? scoped : shopProducts, "products.query");
              });
            });
        }

        var tagHandle = collectionTagHandle(filterDef);
        if (!tagHandle) return fromStorefront();

        return this.fetchAllProductsLiquid({ tagHandle: tagHandle })
          .then(function (liquid) {
            var result = matchedFrom(liquid, "liquid.tag");
            if (result.products.length) return result;
            return fromStorefront();
          })
          .catch(function (err) {
            if (window.console) console.warn("[BYOB] Liquid tag filter failed:", err);
            return fromStorefront();
          });
      }

      /** Keep shop-wide products(query) results inside the hydrated BYOB catalog when possible. */
      scopeProductsToCatalog(products) {
        if (!products || !products.length) return [];
        if (!this.dataset_products || !this.dataset_products.length) return products.slice();
        var allowed = {};
        this.dataset_products.forEach(function (p) {
          allowed[String(p.productId)] = true;
        });
        return products.filter(function (p) {
          return allowed[String(p.productId)];
        });
      }

      storefrontEndpoint() {
        var version = this.config.apiVersion || "2025-01";
        return ROOT + "api/" + version + "/graphql.json";
      }

      storefrontFetch(query, variables) {
        var cfg = this.config;
        return fetch(this.storefrontEndpoint(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": cfg.token
          },
          body: JSON.stringify({ query: query, variables: variables || {} })
        }).then(function (r) {
          return r.json();
        }).then(function (json) {
          if (json && json.errors && window.console) {
            console.warn("[BYOB] Storefront GraphQL errors:", json.errors);
          }
          return json;
        });
      }

      fetchCollectionProducts(opts) {
        opts = opts || {};
        var cfg = this.config;
        var handle = this.getActiveCollectionHandle() || cfg.collectionHandle;
        if (!cfg.token || !handle) return Promise.resolve([]);

        var pageSize = Math.min(parseInt(cfg.pageSize, 10) || 50, 250);
        var maxProducts = 1000;
        var collected = [];
        var bestSellingIndex = 0;
        var filters = opts.filters && opts.filters.length ? opts.filters : null;
        var sortKey = opts.sortKey || "BEST_SELLING";
        var reverse = !!opts.reverse;
        var self = this;
        var useFilters = !!filters;

        var query = useFilters
          ? "query($handle:String!,$first:Int!,$after:String,$filters:[ProductFilter!]!,$sortKey:ProductCollectionSortKeys,$reverse:Boolean){" +
            "collection(handle:$handle){products(first:$first,after:$after,filters:$filters,sortKey:$sortKey,reverse:$reverse){" +
            "pageInfo{hasNextPage endCursor}" +
            "nodes{" +
            STOREFRONT_PRODUCT_FIELDS +
            "}}}}"
          : "query($handle:String!,$first:Int!,$after:String,$sortKey:ProductCollectionSortKeys,$reverse:Boolean){" +
            "collection(handle:$handle){products(first:$first,after:$after,sortKey:$sortKey,reverse:$reverse){" +
            "pageInfo{hasNextPage endCursor}" +
            "nodes{" +
            STOREFRONT_PRODUCT_FIELDS +
            "}}}}";

        function page(after) {
          var variables = {
            handle: handle,
            first: pageSize,
            after: after || null,
            sortKey: sortKey,
            reverse: reverse
          };
          if (useFilters) variables.filters = filters;

          return self
            .storefrontFetch(query, variables)
            .then(function (json) {
              if (json && json.errors && json.errors.length && !json.data) {
                throw new Error(
                  (json.errors[0] && json.errors[0].message) || "Storefront collection query failed"
                );
              }
              var coll = json && json.data && json.data.collection;
              if (!coll || !coll.products) return collected;
              var conn = coll.products;
              conn.nodes.forEach(function (node) {
                var mapped = mapStorefrontProductNode(node, bestSellingIndex++);
                if (mapped) collected.push(mapped);
              });
              if (conn.pageInfo.hasNextPage && collected.length < maxProducts) {
                return page(conn.pageInfo.endCursor);
              }
              return collected;
            });
        }

        return page(null);
      }

      /**
       * Top-level products(query:, sortKey:) — tag filtering + sorting via search syntax.
       * @see https://shopify.dev/docs/api/storefront/latest/queries/products
       * @see https://shopify.dev/docs/api/storefront/latest/enums/ProductSortKeys
       */
      fetchShopProductsQuery(filterDef, sortOpts) {
        var cfg = this.config;
        if (!cfg.token) return Promise.resolve([]);

        var pageSize = Math.min(parseInt(cfg.pageSize, 10) || 50, 250);
        var maxProducts = 1000;
        var collected = [];
        var bestSellingIndex = 0;
        var searchQuery = buildProductsSearchQuery(filterDef);
        var sort = sortOpts || resolveSortOption(this.activeSort);
        var self = this;

        var query =
          "query($first:Int!,$after:String,$query:String,$sortKey:ProductSortKeys,$reverse:Boolean){" +
          "products(first:$first,after:$after,query:$query,sortKey:$sortKey,reverse:$reverse){" +
          "pageInfo{hasNextPage endCursor}" +
          "nodes{" +
          STOREFRONT_PRODUCT_FIELDS +
          "}}}";

        function page(after) {
          return self
            .storefrontFetch(query, {
              first: pageSize,
              after: after || null,
              query: searchQuery,
              sortKey: sort.productSortKey,
              reverse: sort.reverse
            })
            .then(function (json) {
              var conn = json && json.data && json.data.products;
              if (!conn) return collected;
              conn.nodes.forEach(function (node) {
                var mapped = mapStorefrontProductNode(node, bestSellingIndex++);
                if (mapped) collected.push(mapped);
              });
              if (conn.pageInfo.hasNextPage && collected.length < maxProducts) {
                return page(conn.pageInfo.endCursor);
              }
              return collected;
            });
        }

        return page(null);
      }

      fetchAllProducts() {
        return this.fetchCollectionProducts({ sortKey: "BEST_SELLING" });
      }

      /* ---------- Cart ---------- */
      addBundleToCart() {
        if (this.getSelectionCount() === 0) return;
        var self = this;
        var bundleId = "byob-" + Date.now().toString(36);
        var discountPct = this.currentDiscountPct();
        var bundleName = self.config.bundleTitle ? String(self.config.bundleTitle).trim() : "";
        var bundleImageUrl = self.config.bundleImageUrl ? String(self.config.bundleImageUrl).trim() : "";
        var items = Array.from(this.selection.values()).map(function (p) {
          var properties = {
            _byob_bundle: bundleId,
            _byob_discount_pct: String(discountPct),
            _byob_qty_total: String(self.getSelectionCount())
          };
          if (bundleName) properties._byob_bundle_name = bundleName;
          if (bundleImageUrl) properties._byob_bundle_img_url = bundleImageUrl;
          return {
            id: parseInt(p.variantId, 10),
            quantity: p.qty || 1,
            properties: properties
          };
        });

        this.els.addBtn.setAttribute("aria-busy", "true");
        this.els.addBtn.disabled = true;

        var sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:prepare-bundled-sections", {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        fetch(ROOT + "cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({
            items: items,
            sections: sectionsToBundle.join(","),
            sections_url: window.location.pathname
          })
        })
          .then(function (res) {
            return res.json().then(function (json) {
              if (!res.ok) throw new Error(json.description || "Add to cart failed");
              return json;
            });
          })
          .then(function (responseJson) {
            return fetch(ROOT + "cart.js")
              .then(function (r) {
                return r.json();
              })
              .then(function (cartContent) {
                cartContent.sections = responseJson.sections;
                document.documentElement.dispatchEvent(
                  new CustomEvent("cart:change", {
                    bubbles: true,
                    detail: { baseEvent: "variant:add", onSuccessDo: "force_open_drawer", cart: cartContent }
                  })
                );
                document.dispatchEvent(new CustomEvent("cart:refresh"));
                self.resetSelection();
              });
          })
          .catch(function (err) {
            if (window.console) console.error("[BYOB] add to cart error:", err);
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:error", { bubbles: true, detail: { error: String(err.message || err) } })
            );
          })
          .then(function () {
            self.els.addBtn.removeAttribute("aria-busy");
            self.els.addBtn.disabled = self.getSelectionCount() === 0;
          });
      }

      resetSelection() {
        this.selection.clear();
        this.collapseCartDetails();
        this.afterSelectionChange();
      }
    };

    if (!window.customElements.get("byob-builder")) {
      window.customElements.define("byob-builder", ByobBuilder);
    }
  }
})();
